<template>
  <teleport to="body">
    <Modal
      v-model:is-open="generic_delete_modal_open"
      max-width="max-w-[1000px] min-h-[200px]"
      :default-success-msg="generic_delete_item?.success_message"
      :use-toast="true">
      <template #form="{ close: closeModal, done: showIsDone }">
        <h3 class="text-xl font-bold text-gray-900 dark:text-white">
          {{ generic_delete_item?.title }}
        </h3>
        <p class="mt-4 text-gray-600 dark:text-gray-300">
          {{ generic_delete_item?.message }}
        </p>
        <div class="mt-6 flex justify-center space-x-4">
          <button
            type="button"
            class="px-6 py-3 text-red-700 bg-red-100 rounded-md hover:bg-red-200"
            @click="
              () => {
                generic_delete_item?.action()
                showIsDone()
              }
            ">
            {{ $t("Delete") }}
          </button>
          <button type="button" class="px-6 py-3 border rounded-md dark:text-white" @click="closeModal">{{ $t("Cancel") }}</button>
        </div>
      </template>
    </Modal>
    <Modal
      v-model:is-open="survey_responses_modal_open"
      max-width="max-w-[1000px] min-h-[500px]"
      :default-success-msg="$t('Survey Successfully sent')"
      :use-toast="true">
      <template #form="{ close: closeModal, done: showIsDone }">
        <div class="flex flex-col h-full px-4 w-full">
          <SurveyResponses
            :survey="survey"
            @done="
              () => {
                showIsDone()
                survey_responses_modal_open = false
              }
            "
            @cancel="closeModal" />
        </div>
      </template>
    </Modal>

    <Modal v-model:is-open="select_survey_modal_open" :default-success-msg="$t('Survey Successfully sent')" :use-toast="true">
      <template #form="{ close: closeModal, done: showIsDone }">
        <div class="flex flex-col items-center h-full px-4">
          <SurveySelectionForm
            :records="records"
            @done="
              () => {
                showIsDone()
                select_survey_modal_open = false
              }
            "
            @cancel="closeModal" />
        </div>
      </template>
    </Modal>

    <Modal
      v-model:is-open="survey_modal_open"
      :default-success-msg="survey ? $t('Survey successfully modified') : $t('New Survey successfully created')"
      :use-toast="true">
      <template #form="{ close: closeModal, done: showIsDone }">
        <div class="flex flex-col h-full px-4">
          <SurveyForm
            :records="records"
            :survey="survey"
            @done="
              () => {
                showIsDone()
                survey_modal_open = false
                if (select_survey_modal_open_after_submission) {
                  select_survey_modal_open = true
                  select_survey_modal_open_after_submission = false
                }
              }
            "
            @cancel="closeModal" />
        </div>
      </template>
    </Modal>

    <Modal v-model:is-open="send_survey_modal_open" :default-success-msg="$t('New Survey successfully created')" :use-toast="true">
      <template #form="{ close: closeModal, done: showIsDone }">
        <div class="flex flex-col h-full px-4">
          <SurveySendForm
            :survey="survey"
            @done="
              () => {
                showIsDone()
                send_survey_modal_open = false
              }
            "
            @cancel="closeModal" />
        </div>
      </template>
    </Modal>

    <Modal v-model:is-open="success_modal_open">
      <template #form="{ close: closeModal }">
        <div class="flex flex-col items-center justify-center h-full px-4 text-center">
          <div class="flex justify-center">
            <CircleCheck class="w-20 h-20 m-10 text-green-400" aria-hidden="true" />
          </div>
          <h2 class="text-3xl font-extrabold text-gray-900 dark:text-white">
            {{ $t("Successful") }}
          </h2>
          <p class="mt-4 text-xl text-gray-500">
            {{ success_modal_message }}
          </p>
          <button
            type="button"
            class="inline-flex items-center justify-center px-4 py-2 mt-10 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm group hover:bg-gray-50 focus:outline-none"
            @click="closeModal">
            {{ $t("Close") }}
          </button>
        </div>
      </template>
    </Modal>

    <Modal v-model:is-open="map_custom_fields_modal_open" max-width="max-w-[600px] mx-5">
      <template #form="{}">
        <div>
          <div class="bg-gradient-to-b from-[#B0D0FF]/70 h-36 w-[100%] absolute rounded-t-lg left-0 top-0 pointer-events-none" />

          <h3 class="relative z-20 flex items-center w-full text-2xl font-medium leading-6 text-gray-900 dark:text-dark-body-text">
            <span> <MapFieldsIcon color="black" class="mr-4 w-7 h-7" /> </span>{{ $t("Map Custom Fields") }}
          </h3>
          <div class="relative z-20 max-w-xl mt-2 text-sm text-gray-500">
            <p>{{ $t("Select which records to be mapped for the below fields.") }}</p>
          </div>
          <div class="w-full mt-5 text-center">
            <div class="flex items-center w-full space-x-4">
              <div class="text-start">
                <div class="w-28">
                  {{ $t("First Name") }}
                </div>
                <div class="absolute text-sm text-common-grey">
                  ({{ $t("optional") }})
                </div>
              </div>
              <div>
                <LongArrowIcon />
              </div>
              <div>
                <AutoCompleteInput
                  v-if="
                    $filter($store.state.metrics, {
                      object_type: map_custom_record_object_type
                    }).length
                  "
                  :value="
                    map_custom_record_all_metrics?.find((m) => m.reference_name == map_custom_fields_record_to_map?.value?.first_name)?.name
                  "
                  :data="
                    $filter(map_custom_record_all_metrics, {
                      object_type: map_custom_record_object_type
                    })
                  "
                  :placeholder="$t('Search for a Metric')"
                  @reset="map_custom_fields_record_to_map.value['first_name'] = null"
                  @chosen="chooseFirstName($event, map_custom_fields_record_to_map)" />
              </div>
            </div>

            <div class="flex items-center w-full mt-6 space-x-4">
              <div class="text-start">
                <div class="w-28">
                  {{ $t("Last Name") }}
                </div>
                <div class="absolute text-sm text-common-grey">
                  ({{ $t("optional") }})
                </div>
              </div>
              <div>
                <LongArrowIcon />
              </div>
              <div>
                <AutoCompleteInput
                  v-if="
                    $filter($store.state.metrics, {
                      object_type: map_custom_record_object_type
                    }).length
                  "
                  :value="
                    map_custom_record_all_metrics?.find((m) => m.reference_name == map_custom_fields_record_to_map?.value?.last_name)?.name
                  "
                  :data="
                    $filter(map_custom_record_all_metrics, {
                      object_type: map_custom_record_object_type
                    })
                  "
                  :placeholder="$t('Search for a Metric')"
                  @chosen="chooseLastName($event, map_custom_fields_record_to_map)"
                  @reset="map_custom_fields_record_to_map.value['last_name'] = null" />
              </div>
            </div>

            <div class="flex items-center w-full mt-6 space-x-4">
              <div class="text-start">
                <div class="w-28">
                  {{ $t("E-mail address") }}
                </div>
              </div>

              <div>
                <LongArrowIcon />
              </div>
              <div>
                <AutoCompleteInput
                  v-if="
                    $filter($store.state.metrics, {
                      object_type: map_custom_record_object_type
                    }).length
                  "
                  :value="
                    map_custom_record_all_metrics?.find((m) => m.reference_name == map_custom_fields_record_to_map?.value?.email)?.name
                  "
                  :data="
                    $filter(map_custom_record_all_metrics, {
                      object_type: map_custom_record_object_type
                    })
                  "
                  :placeholder="$t('Search for a Metric')"
                  @chosen="chooseEmail($event, map_custom_fields_record_to_map)"
                  @reset="map_custom_fields_record_to_map.value['email'] = null" />
              </div>
            </div>
          </div>

          <div class="flex w-full mt-6">
            <button
              class="ml-auto btn primary-btn w-fit"
              :class="[
                {
                  'pointer-events-none opacity-60': !map_custom_fields_record_to_map.value['email']
                }
              ]"
              @click="map_custom_fields_modal_open = false">
              {{ $t("Done") }}
            </button>
          </div>
        </div>
      </template>
    </Modal>

    <Modal v-model:is-open="integration_missing_modal">
      <template #form="{ close: closeModal }">
        <div class="flex flex-col items-center justify-center h-full px-6 py-8">
          <!-- Heroicon link icon -->
          <div class="mb-4">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
              class="w-16 h-16">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M6.75 2.994v2.25m10.5-2.25v2.25m-14.252 13.5V7.491a2.25 2.25 0 0 1 2.25-2.25h13.5a2.25 2.25 0 0 1 2.25 2.25v11.251m-18 0a2.25 2.25 0 0 0 2.25 2.25h13.5a2.25 2.25 0 0 0 2.25-2.25m-18 0v-7.5a2.25 2.25 0 0 1 2.25-2.25h13.5a2.25 2.25 0 0 1 2.25 2.25v7.5m-6.75-6h2.25m-9 2.25h4.5" />
            </svg>
          </div>

          <!-- Main message -->
          <p class="text-lg font-medium text-gray-700 mb-6 text-center">
            {{ $t("Connect your account to Google Calendar or Outlook Calendar to send a meeting invite.") }}
          </p>

          <!-- Google Calendar Button with Icon -->
          <div class="w-full flex justify-center mb-4">
            <button
              type="button"
              class="flex items-center justify-center gap-x-2 w-64 px-4 py-2 text-sm font-medium text-gray-900 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2"
              @click="
                () => {
                  handleAddIntegrations('google-calendar')
                  closeModal()
                }
              ">
              <img src="https://storage.googleapis.com/revsetter-public/integrations-logos/gmail.png" alt="Google Calendar" class="h-6">
              {{ $t("Link Google Calendar") }}
            </button>
          </div>

          <!-- Outlook Calendar Button with Icon -->
          <div class="w-full flex justify-center mb-4">
            <button
              type="button"
              class="flex items-center justify-center gap-x-2 w-64 px-4 py-2 text-sm font-medium text-gray-900 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2"
              @click="
                () => {
                  handleAddIntegrations('outlook')
                  closeModal()
                }
              ">
              <img
                src="https://storage.googleapis.com/revsetter-public/integrations-logos/outlook.png"
                alt="Outlook Calendar"
                class="h-6">
              {{ $t("Link Outlook Calendar") }}
            </button>
          </div>

          <!-- Cancel button -->
          <div class="flex justify-center mt-6">
            <button
              type="button"
              class="flex items-center justify-center w-32 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md shadow-sm hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2"
              @click="closeModal">
              {{ $t("Cancel") }}
            </button>
          </div>
        </div>
      </template>
    </Modal>
    <!--  Create New INTERACTION -->

    <Modal
      v-model:is-open="new_interaction_modal_is_open"
      :default-success-msg="$t('New Interaction successfully created')"
      :use-toast="true"
      :max-width="interaction_modal_width">
      <template #form="{ close: closeModal, done: showIsDone, error: showError }">
        <h3 class="text-2xl font-medium leading-10 text-left text-gray-800 dark:text-dark-body-text">
          {{ $t("New Interaction") }}
        </h3>
        <CreateNewInteraction
          :count="total_records_count"
          :object-type="current_object_type"
          :excluded-records="excluded_records"
          :is-all-records="is_all_records"
          :contact="selected_contact"
          :records="records"
          :record="record"
          :is-edit-template="is_edit_template"
          :selected-template="selected_template"
          @done="
            () => {
              showIsDone()
              new_interaction_modal_is_open = false
            }
          "
          @error="showError"
          @cancel="
            () => {
              new_interaction_modal_is_open = false
              closeModal()
            }
          " />
      </template>

      <template #error="{ close: closeModal }">
        <div class="flex flex-col items-center justify-center h-full px-4 text-center">
          <div class="flex justify-center">
            <CircleAlert class="w-20 h-20 m-10 text-red-400" aria-hidden="true" />
          </div>
          <h2 class="text-3xl font-extrabold text-gray-900 dark:text-dark-body-text">
            {{ $t("Error") }}
          </h2>
          <p class="mt-4 text-xl text-gray-500">
            {{ $t("Error adding interaction, please try again!") }}
          </p>
          <button
            type="button"
            class="inline-flex items-center justify-center px-4 py-2 mt-10 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm group dark:text-dark-body-text dark:bg-dark-box-background dark:border-dark-divider-border hover:bg-gray-50 focus:outline-none"
            @click="closeModal">
            {{ $t("Close") }}
          </button>
        </div>
      </template>
    </Modal>

    <!-- Create GROUP modal  -->

    <Modal v-model:is-open="new_group_modal_is_open" :default-success-msg="$t('Group created successfully')" :use-toast="true">
      <template #form="{ close: closeModel, done: showIsDone }">
        <h3 class="text-2xl font-medium leading-10 text-left text-gray-900 dark:text-dark-body-text">
          {{ $t("Create Group") }}
        </h3>
        <NewGroupForm :filter="current_filter" :object-type="current_object_type" @cancel="closeModel" @done="showIsDone" />
      </template>
    </Modal>

    <!-- New GOAL Drawer -->

    <Modal
      v-model:is-open="new_goal_modal_is_open"
      :max-width="goal_modal_width"
      :default-success-msg="$t('Great! You have successfully added a goal')"
      :use-toast="true">
      <template #form="{ done: showIsDone, error: showError }">
        <h3 class="text-2xl font-medium leading-10 text-left text-gray-800 dark:text-dark-body-text">
          {{ $t("New Goal") }}
        </h3>
        <NewGoalForm
          :count="total_records_count"
          :object-type="current_object_type"
          :excluded-records="excluded_records"
          :is-all-records="is_all_records"
          :records="records"
          :record="record"
          :is-in-plan="in_plan"
          :is-in-milestone="in_milestone"
          :contact="selected_contact"
          :is-edit-template="is_edit_template"
          :selected-template="selected_template"
          @done="
            () => {
              showIsDone()
              closeCallBack()
              new_goal_modal_is_open = false
            }
          "
          @error="showError"
          @cancel="
            () => {
              new_goal_modal_is_open = false
              closeCallBack()
            }
          " />
      </template>

      <template #error="{ close: closeModal }">
        <div class="flex flex-col items-center justify-center h-full px-4 text-center">
          <div class="flex justify-center">
            <CircleAlert class="w-20 h-20 m-10 text-red-400" aria-hidden="true" />
          </div>
          <h2 class="text-3xl font-extrabold text-gray-900 dark:text-dark-body-text">
            {{ $t("Error") }}
          </h2>
          <p class="mt-4 text-xl text-gray-500">
            {{ $t("Error adding Goal, please try again!") }}
          </p>
          <button
            type="button"
            class="inline-flex items-center justify-center px-4 py-2 mt-10 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm group dark:text-dark-body-text dark:bg-dark-box-background dark:border-dark-divider-border hover:bg-gray-50 focus:outline-none"
            @click="
              () => {
                closeModal()
                closeCallBack()
              }
            ">
            {{ $t("Close") }}
          </button>
        </div>
      </template>
    </Modal>

    <!-- New OWNER Drawer -->
    <Drawer
      v-model:is-open="new_owner_modal_is_open"
      :use-toast="true"
      :show-x-button="true"
      class="z-50"
      title="Assign New Owner"
      :default-success-msg="$t('New Owner successfully assigned')">
      <template #form="{ close: closeDrawer, done: showIsDone }">
        <AssignNewOwner
          :count="total_records_count"
          :object-type="current_object_type"
          :excluded-records="excluded_records"
          :is-all-records="is_all_records"
          :filter="current_filter"
          :records="records"
          :record="record"
          :full-records="full_records"
          @cancel="closeDrawer"
          @done="showIsDone" />
      </template>
    </Drawer>

    <!-- New OWNER Drawer -->
    <DrawerFixedFooter
      v-model:is-open="share_page_is_open"
      :use-toast="true"
      class="z-50"
      :default-success-msg="$t('Page Shared Successfully')">
      <template #form="{ close: closeDrawer }">
        <SharePage :page="shared_page" @cancel="closeDrawer" @done="closeDrawer" />
      </template>
    </DrawerFixedFooter>
    <!-- New OWNER Drawer -->
    <DrawerFixedFooter
      v-model:is-open="clone_page_is_open"
      :use-toast="true"
      class="z-50"
      :default-success-msg="$t('Page Template Shared Successfully')">
      <template #form="{ close: closeDrawer }">
        <ClonePage :page="cloned_page" @cancel="closeDrawer" @done="closeDrawer" />
      </template>
    </DrawerFixedFooter>

    <!-- Co OWNER Drawer -->
    <DrawerFixedFooter
      v-model:is-open="co_owner_modal_is_open"
      class="z-50"
      :use-toast="true"
      :default-success-msg="$t('Co-Owners successfully updated')"
      title="Change Co-Owners">
      <template #form="{ close: closeDrawer, done: showIsDone }">
        <AssignCoOwners
          :filter="current_filter"
          :count="total_records_count"
          :object-type="current_object_type"
          :excluded-records="excluded_records"
          :is-all-records="is_all_records"
          :record="record"
          :records="records"
          :full-records="full_records"
          @cancel="closeDrawer"
          @done="showIsDone" />
      </template>
    </DrawerFixedFooter>

    <!-- Create New TASK Drawer -->

    <Modal
      v-model:is-open="new_task_modal_is_open"
      :max-width="goal_modal_width"
      :use-toast="true"
      :default-success-msg="$t('Great! You have successfully added a task')">
      <template #form="{ close: closeModal, done: showIsDone, error: showError }">
        <h3 class="text-2xl font-medium leading-10 text-gray-800 dark:text-dark-body-text">
          {{ $t("Add Task") }}
        </h3>
        <CreateNewTask
          :defaults="new_task_defaults"
          :count="total_records_count"
          :object-type="current_object_type"
          :excluded-records="excluded_records"
          :is-all-records="is_all_records"
          :records="records"
          :contact="selected_contact"
          :record="record"
          :is-in-plan="in_plan"
          :is-in-goal="in_goal"
          :is-in-interaction="in_interaction"
          :is-in-milestone="in_milestone"
          :is-edit-template="is_edit_template"
          :selected-template="selected_template"
          @done="
            () => {
              new_task_modal_is_open = false
              showIsDone()
              closeCallBack()
            }
          "
          @error="
            () => {
              showError()
              closeCallBack()
            }
          "
          @cancel="
            () => {
              new_task_modal_is_open = false
              closeModal()
              closeCallBack()
            }
          " />
      </template>

      <template #error="{ close: closeModal }">
        <div class="flex flex-col items-center justify-center h-full px-4 text-center">
          <div class="flex justify-center">
            <CircleAlert class="w-20 h-20 m-10 text-red-400" aria-hidden="true" />
          </div>
          <h2 class="text-3xl font-extrabold text-gray-900 dark:text-dark-body-text">
            {{ $t("Error") }}
          </h2>
          <p class="mt-4 text-xl text-gray-500">
            {{ $t("Error adding task, please try again!") }}
          </p>
          <button
            type="button"
            class="inline-flex items-center justify-center px-4 py-2 mt-10 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm group dark:text-dark-body-text dark:bg-dark-box-background dark:border-dark-divider-border hover:bg-gray-50 focus:outline-none"
            @click="
              () => {
                closeModal()
                closeCallBack()
              }
            ">
            {{ $t("Close") }}
          </button>
        </div>
      </template>
    </Modal>

    <!-- Assign Tag Drawer -->

    <Modal v-model:is-open="assign_tags_modal_is_open" :use-toast="true" :default-success-msg="$t('You have successfully assigned tags')">
      <template #form="{ close: closeModal, done: showIsDone, error: showError }">
        <assignTags
          :count="total_records_count"
          :records="records"
          :excluded-records="excluded_records"
          :is-all-records="is_all_records"
          :record="record"
          :full-records="full_records"
          :object-type="current_object_type"
          :filter="current_filter"
          @done="showIsDone"
          @error="showError"
          @cancel="closeModal" />
      </template>

      <template #error="{ close: closeModal }">
        <div class="flex flex-col items-center justify-center h-full px-4 text-center">
          <div class="flex justify-center">
            <CircleAlert class="w-20 h-20 m-10 text-red-400" aria-hidden="true" />
          </div>
          <h2 class="text-3xl font-extrabold text-gray-900 dark:text-dark-body-text">
            {{ $t("Error") }}
          </h2>
          <p class="mt-4 text-xl text-gray-500">
            {{ $t("Error assigning tags, please try again!") }}
          </p>
          <button
            type="button"
            class="inline-flex items-center justify-center px-4 py-2 mt-10 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm group dark:text-dark-body-text dark:bg-dark-box-background dark:border-dark-divider-border hover:bg-gray-50 focus:outline-none"
            @click="closeModal">
            {{ $t("Close") }}
          </button>
        </div>
      </template>
    </Modal>

    <!-- Create new playbook -->

    <Modal v-model:is-open="create_playbook_modal_is_open" :use-toast="false" default-success-msg="">
      <template #form="{ close: closeModal, error: showError }">
        <CreateNewPlaybook :playbooks="playbooks"
                           :playbook="playbook"
                           @done="closeModal"
                           @error="showError"
                           @cancel="closeModal" />
      </template>

      <template #error="{ close: closeModal }">
        <div class="flex flex-col items-center justify-center h-full px-4 text-center">
          <div class="flex justify-center">
            <CircleAlert class="w-20 h-20 m-10 text-red-400" aria-hidden="true" />
          </div>
          <h2 class="text-3xl font-extrabold text-gray-900 dark:text-dark-body-text">
            {{ $t("Error") }}
          </h2>
          <p class="mt-4 text-xl text-gray-500">
            {{ $t("Error create playbook, please try again!") }}
          </p>
          <button
            type="button"
            class="inline-flex items-center justify-center px-4 py-2 mt-10 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm group dark:text-dark-body-text dark:bg-dark-box-background dark:border-dark-divider-border hover:bg-gray-50 focus:outline-none"
            @click="closeModal">
            {{ $t("Close") }}
          </button>
        </div>
      </template>
    </Modal>

    <!-- Create new group -->

    <Modal
      v-model:is-open="create_group_modal_is_open"
      max-width="max-w-[750px]"
      :use-toast="true"
      :default-success-msg="$t('You have successfully created group')">
      <template #form="{ close: closeModal, done: showIsDone, error: showError }">
        <CreateNewGroup :object-type="current_object_type" @error="showError" @done="showIsDone" @cancel="closeModal" />
      </template>

      <template #error="{ close: closeModal }">
        <div class="flex flex-col items-center justify-center h-full px-4 text-center">
          <div class="flex justify-center">
            <CircleAlert class="w-20 h-20 m-10 text-red-400" aria-hidden="true" />
          </div>
          <h2 class="text-3xl font-extrabold text-gray-900 dark:text-dark-body-text">
            {{ $t("Error") }}
          </h2>
          <p class="mt-4 text-xl text-gray-500">
            {{ $t("Error creating a group, please try again!") }}
          </p>
          <button
            type="button"
            class="inline-flex items-center justify-center px-4 py-2 mt-10 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm group dark:text-dark-body-text dark:bg-dark-box-background dark:border-dark-divider-border hover:bg-gray-50 focus:outline-none"
            @click="closeModal">
            {{ $t("Close") }}
          </button>
        </div>
      </template>
    </Modal>

    <!-- Create New PLAN Drawer -->

    <Modal
      v-model:is-open="new_plan_modal_is_open"
      :max-width="plan_modal_width"
      :use-toast="true"
      :default-success-msg="$t('Great! You have successfully added a plan')">
      <template #form="{ done: showIsDone, error: showError }">
        <h3 class="text-2xl font-medium leading-10 text-gray-800 dark:text-dark-body-text">
          {{ $t("Create new plan") }}
        </h3>
        <CreatePlan
          :count="total_records_count"
          :object-type="current_object_type"
          :excluded-records="excluded_records"
          :is-all-records="is_all_records"
          :from-expand="from_expand"
          :plan-values="plan_values"
          :record="record"
          :records="records"
          :contact="selected_contact"
          :is-edit-template="is_edit_template"
          :selected-template="selected_template"
          @done="
            () => {
              showIsDone()
              new_plan_modal_is_open = false
            }
          "
          @error="showError"
          @cancel="new_plan_modal_is_open = false" />
      </template>

      <template #error="{ close: closeModal }">
        <div class="flex flex-col items-center justify-center h-full px-4 text-center">
          <div class="flex justify-center">
            <CircleAlert class="w-20 h-20 m-10 text-red-400" aria-hidden="true" />
          </div>
          <h2 class="text-3xl font-extrabold text-gray-900 dark:text-dark-body-text">
            {{ $t("Error") }}
          </h2>
          <p class="mt-4 text-xl text-gray-500">
            {{ $t("Error adding plan, please try again!") }}
          </p>
          <button
            type="button"
            class="inline-flex items-center justify-center px-4 py-2 mt-10 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm group dark:text-dark-body-text dark:bg-dark-box-background dark:border-dark-divider-border hover:bg-gray-50 focus:outline-none"
            @click="closeModal">
            {{ $t("Close") }}
          </button>
        </div>
      </template>
    </Modal>

    <!-- Custom METRIC Modal -->

    <Modal v-model:is-open="is_created_custom_metric_modal_open" :use-toast="true" :default-success-msg="$t('Metric Created')">
      <template #form="{ done: showIsDone, close: closeModal }">
        <h3 class="text-lg font-medium leading-6 text-gray-900 dark:text-dark-body-text">
          {{ $t("Add Custom Metric") }}
        </h3>
        <NewMetricForm :object-type="current_object_type" @cancel="closeModal" @done="showIsDone" />
      </template>
    </Modal>

    <!-- Feedback Form Modal -->

    <Modal v-model:is-open="is_feedback_form_modal_open" :use-toast="true" :default-success-msg="$t('Feedback shared successfully')">
      <template #form="{ done: showIsDone, close: closeModal }">
        <FeedbackForm @cancel="closeModal" @done="showIsDone" />
      </template>
    </Modal>

    <!-- Welcome Popup -->

    <Modal v-model:is-open="is_onboarding_modal_open" max-width="sm:max-w-4xl">
      <template #form>
        <div class="flex items-start">
          <div class="flex-shrink-0">
            <Info class="h-14 w-14 text-blue-light" aria-hidden="true" />
          </div>
          <div class="ml-3 w-0 flex-1 pt-0.5">
            <p class="text-2xl font-medium text-gray-900 dark:text-dark-body-text">
              {{ $t("Welcome to RevSetter") }} {{ $store.state.auth.user.first_name }}!
            </p>
            <br>
            <div class="mt-1 text-xl leading-9 text-gray-500">
              <p>
                {{ $t("Take your Customer Expansion to the next level!") }}
                <router-link
                  class="underline outline-none appearance-none text-blue whitespace-nowrap ring-blue ring-offset-1 focus:ring-1"
                  to="/admin/integrations/salesforce">
                  {{ $t("Connect your salesforce") }}
                </router-link>
                {{ $t("") }}Or
                <router-link
                  class="underline outline-none appearance-none text-blue whitespace-nowrap ring-blue ring-offset-1 focus:ring-1"
                  to="/admin/spreadsheets/upload">
                  {{ $t("Upload a CSV") }}
                </router-link>
                {{ $t("to get started.") }}
              </p>
              <br>
              {{ $t("If you would like, you can try experimenting your company with some sample data by clicking on") }}
              <router-link
                class="underline outline-none appearance-none text-blue whitespace-nowrap ring-blue ring-offset-1 focus:ring-1"
                to="on-boarding/salesforce?step=2tab=upload">
                {{ $t("Load Sample Data.") }}
              </router-link>

              <p class="text-lg italic text-gray-500">
                {{
                  $t(
                    "You can always link your Salesforce data or upload a CSV later by going to Global Settings - Integrations and following the instructions."
                  )
                }}
              </p>
              <p class="text-lg italic text-gray-500">
                <input
                  v-model="hide_onboarding_modal"
                  type="checkbox"
                  class="w-5 h-5 border-gray-300 rounded focus:ring-blue-light text-blue-light dark:bg-dark-screen-background group-hover:checked:bg-blue-dark">
                {{ $t("Don't show this message again") }}
              </p>
            </div>
          </div>
          <div class="flex flex-shrink-0 ml-4">
            <button
              class="inline-flex text-gray-400 bg-white rounded-md dark:bg-dark-box-background hover:text-gray-500 focus:outline-none"
              @click="is_onboarding_modal_open = false">
              <span class="sr-only">{{ $t("Close") }}</span>
              <X class="w-5 h-5" aria-hidden="true" />
            </button>
          </div>
        </div>
        <div class="flex justify-center mt-5">
          <button type="button" class="w-1/3 primary-btn" @click="closeOnBoarding">{{ $t("Done") }}</button>
        </div>
      </template>
    </Modal>

    <!-- New GRAPH Modal -->

    <Modal v-model:is-open="is_external_score_modal_open" :default-success-msg="$t('NPS generate successfully')" :use-toast="true">
      <!-- in case you want to show the done slot you submitted add done: showIsDone, and call showIsDone instead-->
      <template #form="{ close: closeModal, done: showIsDone }">
        <h3 class="text-2xl font-medium leading-10 text-left text-gray-600 dark:text-dark-body-text">
          {{ $t("Generate NPS") }}
        </h3>
        <NewExternalScore
          :object-type="current_object_type"
          :excluded-records="excluded_records"
          :is-all-records="is_all_records"
          :records="full_records"
          @done="showIsDone"
          @cancel="closeModal" />
      </template>
    </Modal>

    <!-- New GRAPH Modal -->

    <Modal
      v-model:is-open="is_create_graph_modal_open"
      :default-success-msg="$t('Graph created successfully')"
      :use-toast="true"
      @before-close="resetCurrentValues">
      <!-- in case you want to show the done slot you submitted add done: showIsDone, and call showIsDone instead-->
      <template #form="{ close: closeModal, done: showIsDone }">
        <h3 class="text-2xl font-medium leading-10 text-left text-gray-600 dark:text-dark-body-text">
          {{ $t("Add Graph") }}
        </h3>
        <GraphCreateForm
          :initial-filter="current_filter"
          :page-reference="current_page_reference"
          :object-type="current_object_type"
          @done="showIsDone"
          @cancel="closeModal" />
      </template>
    </Modal>

    <!-- Update GRAPH -->

    <Modal
      v-model:is-open="is_update_graph_modal_open"
      :use-toast="true"
      :default-success-msg="$t('Graph updated successfully')"
      @before-close="resetCurrentValues">
      <!-- in case you want to show the done slot you submitted add done: showIsDone, and call showIsDone instead-->
      <template #form="{ close: closeModal, done: showIsDone }">
        <h3 class="text-2xl font-medium leading-10 text-center text-gray-600 dark:text-dark-body-text">
          {{ $t("Update Graph") }}
        </h3>
        <GraphUpdateForm
          :graph="current_graph"
          :page-reference="current_page_reference"
          :object-type="current_object_type"
          @done="showIsDone"
          @cancel="closeModal" />
      </template>
    </Modal>

    <!-- Delete GRAPH -->

    <Modal
      v-model:is-open="is_delete_graph_modal_open"
      :default-success-msg="$t('Graph Deleted')"
      :use-toast="true"
      max-width="xsm:max-w-[600px] mx-5"
      @before-close="resetCurrentValues">
      <template #form="{ close: closeModal, done: showIsDone }">
        <h3 class="text-lg font-medium leading-6 text-gray-900 dark:text-dark-body-text">
          {{ $t("Delete graph") }} {{ current_graph.name }}
        </h3>
        <div class="max-w-xl mt-2 text-sm text-light-body-text dark:text-dark-body-text">
          <p>{{ $t("Once you delete a graph, you can't restore it.") }}</p>
        </div>
        <div class="mt-5 text-center">
          <button
            type="button"
            class="inline-flex items-center justify-center px-4 py-2 ml-2 font-medium border rounded-md dark:text-dark-body-text focus:outline-none focus:ring-2 focus:ring-offset-2 sm:text-sm"
            @click="closeModal">
            {{ $t("Cancel") }}
          </button>
          <button
            type="button"
            class="inline-flex items-center justify-center px-4 py-2 font-medium text-red-700 bg-red-100 border border-transparent rounded-md hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:text-sm"
            @click="confirmDeleteGraph(closeModal, showIsDone)">
            {{ $t("Delete graph") }}
          </button>
        </div>
      </template>
    </Modal>

    <!-- Delete PAGE -->

    <Modal
      v-model:is-open="is_delete_pageh_modal_open"
      :default-success-msg="$t('Page Deleted')"
      :use-toast="true"
      max-width="xsm:max-w-[600px] mx-5"
      @before-close="resetCurrentValues">
      <template #form="{ close: closeModal, done: showIsDone }">
        <h3 class="text-lg font-medium leading-6 text-gray-900 dark:text-dark-body-text">
          {{ page_to_be_deleted.is_static ? "Reset" : "Delete" }} page
          {{ page_to_be_deleted.name }}
        </h3>
        <div class="max-w-xl mt-2 text-sm text-light-body-text dark:text-dark-body-text">
          <p>
            {{ $t("Once you") }} {{ page_to_be_deleted.is_static ? $t("reset") : $t("delete") }} {{ $t("a page, you can't restore it.") }}
          </p>
        </div>
        <div class="mt-5 text-center">
          <button
            type="button"
            class="inline-flex items-center justify-center px-4 py-2 ml-2 mr-2 font-medium border rounded-md dark:text-dark-body-text focus:outline-none focus:ring-2 focus:ring-offset-2 sm:text-sm"
            @click="closeModal">
            {{ $t("Cancel") }}
          </button>
          <button
            type="button"
            class="inline-flex items-center justify-center px-4 py-2 font-medium text-red-700 bg-red-100 border border-transparent rounded-md hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:text-sm"
            @click="confirmDeletePage(closeModal, showIsDone)">
            {{ page_to_be_deleted.is_static ? $t("Reset") : $t("Delete") }} {{ $t("Page") }}
          </button>
        </div>
      </template>
    </Modal>

    <!-- Show/Hide GRAPHS -->

    <Modal v-model:is-open="is_graphs_modal_open" :use-toast="true" :default-success-msg="$t('Graphs updated')">
      <template #form="{ done: showIsDone, close: closeModal }">
        <h3 class="text-lg font-medium leading-6 text-gray-900 dark:text-dark-body-text">
          {{ $t("Show/Hide Graphs") }}
        </h3>

        <MultiSelectInputFlat
          v-model:current-items="updated_graphs"
          :selection-required="false"
          :items="all_graphs"
          :value="current_graphs"
          name="graphs"
          :is-draggable="true"
          value-attr="order"
          label-attr="name"
          :allow-null="false"
          :is-flat="true"
          :return-objects="true"
          :label="$t('Please select what graphs you want to include.')"
          classes="w-full"
          input-classes="sm:text-sm appearance-none font-medium text-gray-700 text-left block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white focus:ring-1 focus:outline-none focus:ring-blue-light focus:border-blue-light "
          :selection-text="null"
          :show-label="true" />
        <div class="flex items-center justify-center gap-2 mt-5 text-center">
          <button type="button" class="inline-flex cancel-btn" @click="closeModal">{{ $t("Cancel") }}</button>

          <button type="button" class="w-40 primary-btn" @click="updatePageGraphs(showIsDone, closeModal)">
            {{ $t("Update Graphs") }}
          </button>
        </div>
      </template>
    </Modal>

    <!-- Update TAble metrics -->

    <Modal v-model:is-open="is_update_forecast_table_modal_open" :use-toast="true" :default-success-msg="$t('Metrics Updated')">
      <template #form="{ done: showIsDone, close: closeModal }">
        <div class="bg-gradient-to-b from-[#B0D0FF]/70 h-36 w-[100%] absolute left-0 top-0 pointer-events-none" />
        <div class="relative z-10 flex space-x-2 text-3xl font-medium">
          <Settings2 class="w-10 h-10 mr-2" color="black" /> {{ $t("Update Forecast Metrics") }}
        </div>
        <div class="relative z-10 ml-12 -mt-2 text-common-grey text-md">
          {{ $t("Please select what metrics you want to include.") }}
        </div>
        <DualList
          v-model:current-items="updated_forecast_table_metrics"
          name="forecast_table_metrics"
          :object-type="current_object_type"
          :show-filters="true"
          :all-items="all_forecast_table_metrics"
          :selected-items="getForecastTableMetrics()"
          :return-objects="true"
          label-attr="label"
          value-attr="id" />
        <div class="flex items-center justify-center gap-2 mt-5 text-center">
          <button type="button" class="inline-flex cancel-btn" @click="closeModal">{{ $t("Cancel") }}</button>
          <button type="button" class="w-40 primary-btn" @click="updateForecastTableMetrics(showIsDone, closeModal)">
            {{ $t("Update") }}
          </button>
        </div>
      </template>
    </Modal>

    <!-- Update Leads table metrics -->

    <Modal v-model:is-open="is_update_leads_table_modal_open" :use-toast="true" :default-success-msg="$t('Metrics Updated')">
      <template #form="{ done: showIsDone, close: closeModal }">
        <div class="bg-gradient-to-b from-[#B0D0FF]/70 h-36 w-[100%] absolute left-0 top-0 pointer-events-none" />
        <div class="relative z-10 flex space-x-2 text-3xl font-medium">
          <Settings2 class="w-10 h-10 mr-2" color="black" /> {{ $t("Update Leads Metrics") }}
        </div>
        <div class="relative z-10 ml-12 -mt-2 text-common-grey text-md">
          {{ $t("Please select what metrics you want to include.") }}
        </div>
        <DualList
          v-model:current-items="updated_leads_table_metrics"
          name="leads_table_metrics"
          :object-type="current_object_type"
          :show-filters="true"
          :all-items="all_leads_table_metrics"
          :selected-items="getCurrentLeadsTableMetrics()"
          :return-objects="true"
          label-attr="label"
          value-attr="id" />

        <div class="flex items-center justify-center gap-2 mt-5 text-center">
          <button type="button" class="inline-flex cancel-btn" @click="closeModal">{{ $t("Cancel") }}</button>
          <button type="button" class="w-40 primary-btn" @click="updateLeadsTableMetrics(showIsDone, closeModal)">
            {{ $t("Update") }}
          </button>
        </div>
      </template>
    </Modal>

    <!-- Update activities table headers -->

    <Modal v-model:is-open="is_update_activities_table_modal_open" :use-toast="true" :default-success-msg="$t('Headers Updated')">
      <template #form="{ done: showIsDone, close: closeModal }">
        <div class="bg-gradient-to-b from-[#B0D0FF]/70 h-36 w-[100%] absolute left-0 top-0 pointer-events-none" />
        <div class="relative z-10 flex space-x-2 text-3xl font-medium">
          <Settings2 class="w-10 h-10 mr-2" color="black" /> {{ $t("Update Headers") }}
        </div>
        <div class="relative z-10 ml-12 -mt-2 text-common-grey text-md">
          {{ $t("Please select what headers you want to include.") }}
        </div>
        <DualList
          v-model:current-items="updated_activities_table_headers"
          name="activities_table_headers"
          :available-items-label="$t('Available headers')"
          :selected-items-label="$t('Included headers')"
          :placeholder-items-label="$t('Search headers')"
          :object-type="current_object_type"
          :show-filters="true"
          :is-activities-header="true"
          :show-search="false"
          :all-items="all_headers"
          :selected-items="getCurrentActivitiesTableHeaders()"
          :return-objects="true"
          label-attr="label"
          value-attr="id" />

        <div class="flex items-center justify-center gap-2 mt-5 text-center">
          <button type="button" class="inline-flex cancel-btn" @click="closeModal">{{ $t("Cancel") }}</button>
          <button type="button" class="w-40 primary-btn" @click="updateActivitiesTableHeaders(showIsDone, closeModal)">
            {{ $t("Update") }}
          </button>
        </div>
      </template>
    </Modal>

    <!-- Add notes in forecasting -->

    <Modal v-model:is-open="is_add_notes_forecasting_modal_open" :use-toast="true" :default-success-msg="$t('Notes saved')">
      <template #form="{ done: showIsDone, close: closeModal }">
        <ForecastNotes :forecast-id="forecast_id" :company="record" @done="showIsDone" @cancel="closeModal" />
      </template>
    </Modal>

    <!-- Add notes to record -->

    <Modal v-model:is-open="is_add_notes_record_modal_open" :use-toast="true" :default-success-msg="$t('Notes saved')">
      <template #form="{ done: showIsDone, close: closeModal }">
        <RecordNotes :company="record" @done="showIsDone" @cancel="closeModal" />
      </template>
    </Modal>

    <!-- Invite team members -->

    <Modal
      v-model:is-open="is_invite_team_members_modal_open"
      max-width="max-w-[950px] !p-0 !m-0"
      :use-toast="true"
      :default-success-msg="$t('The invitation has been sent successfully')">
      <template #form="{ close: closeModal, done: showIsDone, error: showError }">
        <InviteTeammates @error="showError" @done="showIsDone" @cancel="closeModal" />
      </template>
      <template #error="{ close: closeModal }">
        <div class="flex flex-col items-center justify-center h-full p-4 text-center">
          <div class="flex justify-center">
            <CircleAlert class="w-20 h-20 m-10 text-red-400" aria-hidden="true" />
          </div>
          <h2 class="text-3xl font-extrabold text-gray-900 dark:text-dark-body-text">
            {{ $t("Error") }}
          </h2>
          <p class="mt-4 text-xl text-gray-500">
            {{ $t("Error inviting teammates, please try again!") }}
          </p>
          <button
            type="button"
            class="inline-flex items-center justify-center px-4 py-2 mt-10 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm group dark:text-dark-body-text dark:bg-dark-box-background dark:border-dark-divider-border hover:bg-gray-50 focus:outline-none"
            @click="closeModal">
            {{ $t("Close") }}
          </button>
        </div>
      </template>
    </Modal>

    <!-- Delete List -->

    <Modal
      v-model:is-open="is_delete_expansion_list_modal_open"
      :use-toast="true"
      :default-success-msg="$t('List Deleted')"
      @before-close="resetCurrentValues">
      <template #form="{ close: closeModal, done: showIsDone }">
        <h3 class="text-lg font-medium leading-6 text-gray-900">
          {{ $t("Delete List") }}
        </h3>
        <div class="max-w-xl mt-2 text-sm text-gray-500">
          <p>{{ $t("Once you delete this list, you can't restore it.") }}</p>
        </div>
        <div class="mt-5 text-center">
          <button
            type="button"
            class="inline-flex items-center justify-center px-4 py-2 font-medium text-red-700 bg-red-100 border border-transparent rounded-md hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:text-sm"
            @click="confirmDeleteExpnasionList(showIsDone)">
            {{ $t("Delete List") }}
          </button>
          <button
            type="button"
            class="inline-flex items-center justify-center px-4 py-2 ml-2 font-medium border rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 sm:text-sm"
            @click="closeModal">
            {{ $t("Cancel") }}
          </button>
        </div>
      </template>
    </Modal>

    <!-- Remove records from LIST -->

    <Modal
      v-model:is-open="is_remove_from_expansion_list_modal_open"
      :use-toast="true"
      :default-success-msg="$t('Records from List Deleted')"
      @before-close="resetCurrentValues">
      <template #form="{ close: closeModal, done: showIsDone }">
        <h3 class="text-lg font-medium leading-6 text-gray-900 dark:text-white">
          {{ $t("Remove from List") }}
        </h3>
        <div class="max-w-xl mt-2 text-sm text-gray-500">
          <p>{{ $t("Do you want to remove these selected items from this list ?") }}</p>
        </div>
        <div class="mt-5 text-center">
          <button
            type="button"
            class="inline-flex items-center justify-center px-4 py-2 font-medium text-red-700 bg-red-100 border border-transparent rounded-md hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:text-sm"
            @click="confirmRemoveFromExpnasionList(showIsDone)">
            {{ $t("Remove") }}
          </button>
          <button
            type="button"
            class="inline-flex items-center justify-center px-4 py-2 ml-2 font-medium border rounded-md dark:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 sm:text-sm"
            @click="closeModal">
            {{ $t("Cancel") }}
          </button>
        </div>
      </template>
    </Modal>

    <!-- Update TABS -->

    <Modal v-model:is-open="is_update_tabs_modal_open" :use-toast="true" :default-success-msg="$t('Tabs Updated')">
      <template #form="{ done: showIsDone, close: closeModal }">
        <h3 class="text-lg font-medium leading-6 text-gray-900 dark:text-dark-body-text">
          {{ $t("Update") }}
          <span class="capitalize">{{
            $route.params.object_type ? $route.params.object_type : current_page_reference.replace("_", " ")
          }}</span>
          {{ $t("Tabs") }}
        </h3>
        <!-- <DualList :items="all_tabs" v-model:current-items="updated_tabs" :value="current_tabs" label-attr="label" /> -->
        <MultiSelectInputFlat
          v-model:current-items="updated_tabs"
          :items="all_tabs"
          :value="current_tabs"
          name="tabs"
          :is-draggable="true"
          :is-horizontal="true"
          label-attr="label"
          value-attr="id"
          :show-search="true"
          :is-search-expanded="false"
          :allow-null="false"
          :show-label="true"
          :is-flat="true"
          :return-objects="true"
          :label="$t('Please select what tabs you want to include.')"
          classes="w-full"
          input-classes="sm:text-sm appearance-none font-medium text-gray-700 text-left block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white focus:ring-1 focus:outline-none focus:ring-blue-light focus:border-blue-light "
          :selection-text="null" />
        <div class="flex items-center justify-center gap-2 mt-5 text-center">
          <button type="button" class="inline-flex cancel-btn" @click="closeModal">{{ $t("Cancel") }}</button>
          <button type="button" class="w-40 primary-btn" @click="updateTabs(showIsDone, closeModal)">{{ $t("Update Tabs") }}</button>
        </div>
      </template>
    </Modal>

    <!--  METRICS -->

    <Modal v-model:is-open="is_update_table_metrics_modal_open" :use-toast="true" :default-success-msg="$t('View Updated')">
      <template #form="{ done: showIsDone, close: closeModal }">
        <div class="bg-gradient-to-b from-[#B0D0FF]/70 h-36 w-[100%] absolute left-0 top-0 pointer-events-none" />
        <div class="relative z-10 flex space-x-2 text-3xl font-medium">
          <Settings2 class="w-10 h-10 mr-2" color="black" />{{ $t("Update") }} {{ $utils.getObjectTypeByType(current_object_type)?.label }}
          {{ $t("Metrics") }}
        </div>
        <div class="relative z-10 ml-12 -mt-2 text-common-grey text-md">
          {{ $t("Please select what metrics you want to include.") }}
        </div>
        <DualList
          v-model:current-items="updated_table_metrics"
          name="included_metrics"
          :object-type="current_object_type"
          :view-type="current_view_type"
          :show-filters="true"
          :all-items="all_table_metrics"
          :selected-items="current_table_metrics"
          :return-objects="true"
          label-attr="name"
          value-attr="id" />

        <div class="flex items-center justify-center gap-2 mt-5 text-center">
          <button type="button" class="inline-flex cancel-btn" @click="closeModal">{{ $t("Cancel") }}</button>
          <button type="button" class="w-40 primary-btn" @click="updateTableMetrics(showIsDone, closeModal)">{{ $t("Update") }}</button>
        </div>
      </template>
    </Modal>

    <!-- INTERACTION update Modal -->

    <Modal
      v-model:is-open="update_interaction_modal_is_open"
      :use-toast="true"
      :default-success-title="$t('Successful')"
      :default-success-msg="`${is_template ? $t('Interaction template successfully created') : $t('Interaction successfully updated')}`"
      :max-width="interaction_modal_width">
      <template #form="{ done: showIsDone, error: showError }">
        <h3 class="text-2xl font-medium leading-10 text-gray-800 dark:text-dark-body-text">
          {{ $t("Update Interaction") }}
        </h3>
        <UpdateInteractionForm
          :company="$store.state.ui.currentCompany"
          :interaction="current_interaction"
          :is-template="is_template"
          @done="
            () => {
              showIsDone()
              update_interaction_modal_is_open = false
            }
          "
          @error="showError"
          @cancel="update_interaction_modal_is_open = false" />
      </template>

      <template #error="{}">
        <div class="flex flex-col items-center justify-center h-full px-4 text-center">
          <div class="flex justify-center">
            <CircleAlert class="w-20 h-20 m-10 text-red-400" aria-hidden="true" />
          </div>
          <h2 class="text-3xl font-extrabold text-gray-900 dark:text-dark-body-text">
            {{ $t("Error") }}
          </h2>
          <p class="mt-4 text-xl text-gray-500">
            {{ $t("Error updating interaction, please try again!") }}
          </p>
          <button
            type="button"
            class="inline-flex items-center justify-center px-4 py-2 mt-10 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm group dark:text-dark-body-text dark:bg-dark-box-background dark:border-dark-divider-border hover:bg-gray-50 focus:outline-none"
            @click="update_interaction_modal_is_open = false">
            {{ $t("Close") }}
          </button>
        </div>
      </template>
    </Modal>

    <!-- TASK update Modal -->

    <Modal
      v-model:is-open="update_task_modal_is_open"
      :use-toast="true"
      :default-success-title="$t('Successful')"
      :default-success-msg="`${is_template ? $t('Task template successfully created') : $t('Task successfully updated')}`"
      :max-width="task_modal_width">
      <template #form="{ done: showIsDone, error: showError }">
        <h3 class="text-2xl font-medium leading-10 text-gray-800 dark:text-dark-body-text">
          {{ $t("Update Task") }}
        </h3>
        <UpdateTaskForm
          :task="current_task"
          :in-playbook="in_playbook"
          :is-template="is_template"
          @done="
            () => {
              showIsDone()
              update_task_modal_is_open = false
            }
          "
          @error="showError"
          @cancel="update_task_modal_is_open = false" />
      </template>

      <template #error="{}">
        <div class="flex flex-col items-center justify-center h-full px-4 text-center">
          <div class="flex justify-center">
            <CircleAlert class="w-20 h-20 m-10 text-red-400" aria-hidden="true" />
          </div>
          <h2 class="text-3xl font-extrabold text-gray-900">
            {{ $t("Error") }}
          </h2>
          <p class="mt-4 text-xl text-gray-500">
            {{ $t("Error updating task, please try again!") }}
          </p>
          <button
            type="button"
            class="inline-flex items-center justify-center px-4 py-2 mt-10 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm group dark:text-dark-body-text dark:bg-dark-box-background dark:border-dark-divider-border hover:bg-gray-50 focus:outline-none"
            @click="update_task_modal_is_open = false">
            {{ $t("Close") }}
          </button>
        </div>
      </template>
    </Modal>

    <!-- GOAL update Modal -->

    <Modal
      v-model:is-open="update_goal_modal_is_open"
      :use-toast="true"
      :default-success-title="$t('Successful')"
      :default-success-msg="`${is_template ? $t('Goal template successfully created') : $t('Goal successfully updated')}`"
      :max-width="goal_modal_width">
      <template #form="{ done: showIsDone, error: showError }">
        <h3 class="text-2xl font-medium leading-10 text-gray-800 dark:text-dark-body-text">
          {{ $t("Update Goal") }}
        </h3>
        <UpdateGoalForm
          :company="$store.state.ui.currentCompany"
          :goal="current_goal"
          :goals="goals"
          :is-template="is_template"
          @done="
            () => {
              showIsDone()
              update_goal_modal_is_open = false
            }
          "
          @error="showError"
          @cancel="update_goal_modal_is_open = false" />
      </template>

      <template #error="{}">
        <div class="flex flex-col items-center justify-center h-full px-4 text-center">
          <div class="flex justify-center">
            <CircleAlert class="w-20 h-20 m-10 text-red-400" aria-hidden="true" />
          </div>
          <h2 class="text-3xl font-extrabold text-gray-900 dark:text-dark-body-text">
            {{ $t("Error") }}
          </h2>
          <p class="mt-4 text-xl text-gray-500">
            {{ $t("Error updating goal, please try again!") }}
          </p>
          <button
            type="button"
            class="inline-flex items-center justify-center px-4 py-2 mt-10 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm group dark:text-dark-body-text dark:bg-dark-box-background dark:border-dark-divider-border hover:bg-gray-50 focus:outline-none"
            @click="update_goal_modal_is_open = false">
            {{ $t("Close") }}
          </button>
        </div>
      </template>
    </Modal>

    <!-- Plan update Modal -->

    <Modal
      v-model:is-open="update_plan_modal_is_open"
      :use-toast="true"
      :default-success-title="$t('Successful')"
      :default-success-msg="`${is_template ? $t('Plan template successfully created') : $t('Plan successfully updated')}`"
      :max-width="plan_modal_width">
      <template #form="{ done: showIsDone, error: showError }">
        <h3 class="text-2xl font-medium leading-10 text-gray-800 dark:text-dark-body-text">
          {{ $t("Update Plan") }}
        </h3>
        <EditPlan
          :from-expand="from_expand"
          :full-screen="false"
          :company="$store.state.ui.currentCompany"
          :plan="current_plan"
          :is-template="is_template"
          @done="
            () => {
              showIsDone()
              update_plan_modal_is_open = false
            }
          "
          @error="showError"
          @cancel="update_plan_modal_is_open = false" />
      </template>

      <template #done="{}">
        <div class="flex flex-col items-center justify-center h-full px-4 text-center">
          <div class="flex justify-center">
            <CircleCheck class="w-20 h-20 m-10 text-green-400" aria-hidden="true" />
          </div>
          <h2 class="text-3xl font-extrabold text-gray-900 dark:text-dark-body-text">
            {{ $t("Successful") }}
          </h2>
          <p class="mt-4 text-xl text-gray-500">
            {{ $t("Plan successfully updated") }}
          </p>
          <button
            type="button"
            class="inline-flex items-center justify-center px-4 py-2 mt-10 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm group dark:text-dark-body-text dark:bg-dark-box-background dark:border-dark-divider-border hover:bg-gray-50 focus:outline-none"
            @click="update_plan_modal_is_open = false">
            {{ $t("Close") }}
          </button>
        </div>
      </template>

      <template #error="{}">
        <div class="flex flex-col items-center justify-center h-full px-4 text-center">
          <div class="flex justify-center">
            <CircleAlert class="w-20 h-20 m-10 text-red-400" aria-hidden="true" />
          </div>
          <h2 class="text-3xl font-extrabold text-gray-900 dark:text-dark-body-text">
            {{ $t("Error") }}
          </h2>
          <p class="mt-4 text-xl text-gray-500">
            {{ $t("Error updating plan, please try again!") }}
          </p>
          <button
            type="button"
            class="inline-flex items-center justify-center px-4 py-2 mt-10 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm group dark:text-dark-body-text dark:bg-dark-box-background dark:border-dark-divider-border hover:bg-gray-50 focus:outline-none"
            @click="update_plan_modal_is_open = false">
            {{ $t("Close") }}
          </button>
        </div>
      </template>
    </Modal>

    <!-- Rollup METRICS -->

    <!-- didnt work -->

    <Modal v-model:is-open="is_rollup_metrics_modal_open" :use-toast="true" :default-success-msg="$t('Rollup metrics updated')">
      <template #form="{ done: showIsDone, close: closeModal }">
        <h3 class="text-lg font-medium leading-6 text-gray-900 dark:text-dark-body-text">
          {{ $t("Add/Remove Rollup Metrics") }}
        </h3>
        <div class="max-w-xl mt-2 text-sm text-gray-500 dark:text-dark-body-text">
          <p>{{ $t("Please select what rollup metrics you want to include.") }}</p>
        </div>
        <MultiSelectInputFlat
          v-model:current-items="updated_rollup_metrics"
          :selection-required="false"
          :items="all_rollup_metrics"
          :value="current_rollup_metrics"
          name="rollup_metrics"
          :is-draggable="true"
          label-attr="name"
          :allow-null="false"
          :value-attr="'reference_name'"
          :is-flat="true"
          :return-objects="true"
          :label="$t('Rollup Metrics')"
          classes="w-full"
          input-classes="sm:text-sm appearance-none font-medium text-gray-700 text-left block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white focus:ring-1 focus:outline-none focus:ring-blue-light focus:border-blue-light "
          :selection-text="null"
          :show-label="false" />
        <div class="flex items-center justify-center gap-2 mt-5 text-center">
          <button type="button" class="inline-flex cancel-btn" @click="closeModal">{{ $t("Cancel") }}</button>

          <button type="button" class="w-40 primary-btn" @click="updateRollupMetrics(showIsDone, closeModal)">
            {{ $t("Update Metrics") }}
          </button>
        </div>
      </template>
    </Modal>

    <Modal v-model:is-open="show_bulk_records_metric_update_model" :use-toast="true" :default-success-msg="$t('Metrics updated')">
      <template #form="{ done: showIsDone, close: closeModal }">
        <h3 class="text-lg font-medium leading-6 text-gray-900 dark:text-dark-body-text">
          {{ $t("Bulk Metrics Update") }}
        </h3>
        <h5 v-if="is_all_records" class="text-sm font-medium leading-6 text-gray-900 dark:text-dark-body-text mt-2">
          {{ total_records_count - excluded_records.length }} {{ $t("out of") }} {{ total_records_count }}
          {{ $t("records selected in the table") }}
        </h5>
        <h5 v-if="is_all_records" class="text-sm font-medium leading-6 text-gray-900 dark:text-dark-body-text mb-2">
          Any related playbooks will not trigger by this bulk action
        </h5>
        <div class="max-w-xl mt-2 text-sm text-gray-500 dark:text-dark-body-text">
          <p>{{ $t("Please select which metric you want to update.") }}</p>
        </div>
        <div class="flex flex-col gap-2">
          <ComboBoxInput
            v-model:model-value="target_bulk_update_metric"
            :items="$store.state.metrics.filter((x) => x.object_type == current_object_type)"
            :value="target_bulk_update_metric"
            name="metric"
            label-attr="name"
            value-attr="id"
            classes="w-full relative dark:text-white"
            :is-box-background="true"
            :label="$t('Target metric')"
            :show-label="true"
            :floating-label="false"
            :allow-null="false" />

          <MetricFieldInput
            v-show="target_bulk_update_metric"
            v-model:metric-value="target_bulk_update_metric_value"
            :is-bulk-metric="true"
            :metric="$utils.getMetricById(target_bulk_update_metric)"
            :show-success-check-mark="true"
            class="w-full" />

          <div class="flex items-center justify-center gap-2 mt-5 text-center">
            <button type="button" class="inline-flex cancel-btn" @click="closeModal">{{ $t("Cancel") }}</button>

            <button
              type="button"
              class="w-40 primary-btn"
              :disabled="!target_bulk_update_metric"
              @click="bulkRecordsMetricUpdate(showIsDone, closeModal)">
              {{ $t("Update Metrics") }}
            </button>
          </div>
        </div>
      </template>
    </Modal>

    <Modal v-model:is-open="merge_records_metrics_model" :use-toast="true" :default-success-msg="$t('Metrics merging started')">
      <template #form="{ done: showIsDone, close: closeModal }">
        <h3 class="text-lg font-medium leading-6 text-gray-900 dark:text-dark-body-text">
          {{ $t("Metrics merge") }}
        </h3>
        <div class="max-w-xl mt-2 mb-5 text-gray-500 text-md dark:text-dark-body-text">
          <p>{{ $t("Please select two metrics to merge.") }}</p>
        </div>
        <div class="flex flex-col gap-2">
          <div class="flex flex-row justify-between gap-2">
            <ComboBoxInput
              v-model:model-value="merge_metric_a"
              :items="$store.state.metrics.filter((x) => x.object_type == current_object_type)"
              :value="merge_metric_a"
              name="metric"
              label-attr="name"
              value-attr="id"
              classes="w-full relative dark:text-white w-1/2"
              :is-box-background="true"
              :label="$t('Metric One')"
              :show-label="true"
              :floating-label="true"
              :allow-null="false" />
            <ComboBoxInput
              v-model:model-value="merge_metric_b"
              :items="$store.state.metrics.filter((x) => x.object_type == current_object_type)"
              :value="merge_metric_b"
              name="metric"
              label-attr="name"
              value-attr="id"
              classes="w-full relative dark:text-white w-1/2"
              :is-box-background="true"
              :label="$t('Metric Two')"
              :show-label="true"
              :floating-label="true"
              :allow-null="false" />
          </div>
          <SelectInput
            v-model:model-value="merge_metrics_priority"
            name="tag"
            :items="[
              { id: 'none_empty_value', name: $t('Copy none empty values to empty values') },
              { id: 'metric_b', name: $t('Metric B is Source') },
              { id: 'metric_a', name: $t('Metric A is Source') }
            ]"
            label-attr="name"
            :value="merge_metrics_priority"
            value-attr="id"
            :show-label="true"
            :floating-label="true"
            :placeholder="$t('Source Metric')"
            :label="$t('Source Metric')"
            classes="mt-5"
            input-classes="appearance-none text-left block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm" />

          <div class="flex items-center justify-center gap-2 mt-5 text-center">
            <button type="button" class="inline-flex cancel-btn" @click="closeModal">{{ $t("Cancel") }}</button>

            <button
              type="button"
              class="w-40 primary-btn"
              :disabled="!merge_metric_b || !merge_metric_a"
              @click="mergeMetricsValues(showIsDone, closeModal)">
              {{ $t("Merge Metrics") }}
            </button>
          </div>
        </div>
      </template>
    </Modal>

    <!-- Add list Drawer -->
    <Drawer
      v-model:is-open="add_to_expansion_list_modal_is_open"
      :use-toast="true"
      :show-x-button="true"
      class="z-50"
      title="Select list"
      :default-success-msg="$t('list submitted successfully')">
      <template #form="{ close: closeDrawer, done: showIsDone }">
        <NewExpansionList
          :object-type="current_object_type"
          :excluded-records="excluded_records"
          :is-all-records="is_all_records"
          :records="record && records.length ? records : record ? [record?.id] : records"
          :filter="current_filter"
          @cancel="closeDrawer"
          @done="showIsDone" />
      </template>
    </Drawer>

    <!-- Delete Selected Records -->

    <Modal
      v-model:is-open="show_delete_records_confirmation_modal"
      :use-toast="true"
      :default-success-msg="`${$utils.getObjectTypeByType(current_object_type)?.plural_label || current_object_type} ` + $t('Deleted')"
      max-width="max-w-[600px] mx-5">
      <template #form="{ done: showIsDone }">
        <h3 v-if="is_all_records" class="text-lg font-medium leading-6 text-gray-900 dark:text-dark-body-text">
          {{ $t("Delete") }} {{ total_records_count - excluded_records.length }} {{ $t("out of") }} {{ total_records_count }}
          {{ $t("records in table") }}
        </h3>
        <h3 v-else class="text-lg font-medium leading-6 text-gray-900 dark:text-dark-body-text">
          {{ $t("Delete") }} {{ records.length }} {{ $t("selected") }}
          {{ $utils.getObjectTypeByType(current_object_type)?.plural_label || current_object_type }}
        </h3>
        <div class="max-w-xl mt-2 text-sm text-gray-500">
          <p v-if="is_leads_records">
            {{ $t("Once you delete these") }}
            {{ $utils.getObjectTypeByType(current_object_type)?.plural_label || current_object_type }}
            {{
              $t(
                "from your portfolio you can't restore them. If you want to hide this record from the Leads table, adjust the Growth Matrix/ Suggested Leads settings."
              )
            }}
          </p>
          <p v-else>
            {{ $t("Once you delete these") }}
            {{ $utils.getObjectTypeByType(current_object_type)?.plural_label || current_object_type }}
            {{ $t("you can't restore them.") }}
          </p>
        </div>
        <div class="flex items-center justify-center gap-2 mt-5 text-center">
          <button type="button" class="inline-flex cancel-btn" @click="show_delete_records_confirmation_modal = false">
            {{ $t("Cancel") }}
          </button>
          <button
            v-if="is_leads_records"
            type="button"
            class="inline-flex cancel-btn !-ml-0"
            @click="
              () => {
                show_delete_records_confirmation_modal = false
                $router.push(`/organization?tab=suggested`)
              }
            ">
            {{ $t("View Suggested/Growth Matrix") }}
          </button>
          <button
            type="button"
            class="inline-flex items-center justify-center px-4 py-2 font-medium text-red-700 bg-red-100 border border-transparent rounded-md hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:text-sm"
            @click="deleteSelectedRecords(showIsDone)">
            {{ $t("Delete") }}
            {{ $utils.getObjectTypeByType(current_object_type)?.plural_label || current_object_type }}
          </button>
        </div>
      </template>
    </Modal>

    <!-- Delete Group -->

    <Modal
      v-model:is-open="show_delete_group_confirmation_modal"
      :use-toast="true"
      :default-success-msg="$t('Group Deleted')"
      max-width="max-w-[600px] mx-5">
      <template #form="{ done: showIsDone }">
        <h3 class="text-lg font-medium leading-6 text-gray-900 dark:text-dark-body-text">
          {{ $t("Delete group") }} {{ group?.name }}
        </h3>
        <div class="max-w-xl mt-2 text-sm text-gray-500">
          <p>{{ $t("Once you delete a group, you can't restore it.") }}</p>
        </div>
        <div class="flex items-center justify-center gap-2 mt-5 text-center">
          <button type="button" class="inline-flex cancel-btn" @click="show_delete_group_confirmation_modal = false">
            {{ $t("Cancel") }}
          </button>
          <button
            type="button"
            class="inline-flex items-center justify-center px-4 py-2 font-medium text-red-700 bg-red-100 border border-transparent rounded-md hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:text-sm"
            @click="deleteSelectedGroup(showIsDone)">
            {{ $t("Delete group") }}
          </button>
        </div>
      </template>
    </Modal>

    <!-- Delete Template -->

    <Modal
      v-model:is-open="show_delete_template_confirmation_modal"
      :use-toast="true"
      :default-success-msg="$t('Template Deleted')"
      max-width="max-w-[600px] mx-5">
      <template #form="{ done: showIsDone }">
        <h3 class="text-lg font-medium leading-6 text-gray-900 dark:text-dark-body-text">
          {{ $t("Delete template") }} {{ template?.name }}
        </h3>
        <div class="max-w-xl mt-2 text-sm text-gray-500">
          <p>{{ $t("Once you delete a template, you can't restore it.") }}</p>
        </div>
        <div class="flex items-center justify-center gap-2 mt-5 text-center">
          <button type="button" class="inline-flex cancel-btn" @click="show_delete_template_confirmation_modal = false">
            {{ $t("Cancel") }}
          </button>
          <button
            type="button"
            class="inline-flex items-center justify-center px-4 py-2 font-medium text-red-700 bg-red-100 border border-transparent rounded-md hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:text-sm"
            @click="deleteSelectedTemplate(showIsDone)">
            {{ $t("Delete template") }}
          </button>
        </div>
      </template>
    </Modal>

    <!-- Edit Group -->

    <Modal v-model:is-open="show_edit_group_confirmation_modal" :default-success-msg="$t('Group Edited')" :use-toast="true">
      <template #form="{ done: showIsDone }">
        <h3 class="text-lg font-medium leading-6 text-gray-900 dark:text-dark-body-text">
          {{ $t("Edit group") }} {{ group?.name }}
        </h3>
        <UpdateGroupForm :filter="current_filter" :group="group" @cancel="show_edit_group_confirmation_modal = false" @done="showIsDone" />
      </template>
    </Modal>

    <!-- delete  Interaction modal  -->

    <Modal v-model:is-open="delete_interaction_modal_is_open" :use-toast="true" :default-success-msg="$t('Interaction Deleted')">
      <template #form="{ done: showIsDone }">
        <button
          class="inset-y-0 right-0 flex items-center justify-center float-right w-6 h-6 ml-5 rounded-full group hover:bg-gray-300"
          type="button"
          @click="delete_interaction_modal_is_open = false">
          <X class="w-5 h-5 text-gray-400 group-hover:cursor-pointer" aria-hidden="true" />
        </button>
        <h3 class="text-lg font-medium leading-6 text-gray-900 dark:text-dark-body-text">
          {{ $t("Delete Interaction") }}
        </h3>
        <div class="max-w-xl mt-2 text-gray-500 text-md">
          <p>{{ $t("Do you want to delete this interaction ?") }}</p>
        </div>
        <div class="mt-5 text-center">
          <button
            type="button"
            class="inline-flex items-center justify-center px-4 py-2 font-medium text-red-700 bg-red-100 border border-transparent rounded-md hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:text-sm"
            @click="deleteSelectedInteraction(showIsDone)">
            {{ $t("Delete Interaction") }}
          </button>
        </div>
      </template>
    </Modal>

    <!-- delete  TASK modal  -->

    <Modal v-model:is-open="delete_task_modal_is_open" :use-toast="true" :default-success-msg="$t('Task Deleted')">
      <template #form="{ done: showIsDone }">
        <button
          class="inset-y-0 right-0 flex items-center justify-center float-right w-6 h-6 ml-5 rounded-full group hover:bg-gray-300"
          type="button"
          @click="delete_task_modal_is_open = false">
          <X class="w-5 h-5 text-gray-400 group-hover:cursor-pointer" aria-hidden="true" />
        </button>
        <h3 class="text-lg font-medium leading-6 text-gray-900 dark:text-dark-body-text">
          {{ $t("Delete Task") }}
        </h3>
        <div class="max-w-xl mt-2 text-gray-500 text-md">
          <p>{{ $t("Do you want to delete this task ?") }}</p>
        </div>
        <div class="mt-5 text-center">
          <button
            type="button"
            class="inline-flex items-center justify-center px-4 py-2 font-medium text-red-700 bg-red-100 border border-transparent rounded-md hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:text-sm"
            @click="deleteSelectedTask(showIsDone)">
            {{ $t("Delete Task") }}
          </button>
        </div>
      </template>
    </Modal>

    <!-- delete  GOAL modal  -->

    <Modal v-model:is-open="delete_goal_modal_is_open" :default-success-msg="$t('Goal Deleted')">
      <template #form="{ done: showIsDone }">
        <button
          class="inset-y-0 right-0 flex items-center justify-center float-right w-6 h-6 ml-5 rounded-full group hover:bg-gray-300"
          type="button"
          @click="delete_goal_modal_is_open = false">
          <X class="w-5 h-5 text-gray-400 group-hover:cursor-pointer" aria-hidden="true" />
        </button>
        <h3 class="text-lg font-medium leading-6 text-gray-900 dark:text-dark-body-text">
          {{ $t("Delete Goal") }}
        </h3>
        <div class="max-w-xl mt-2 text-gray-500 text-md">
          <p>{{ $t("Do you want to delete this goal ?") }}</p>
        </div>
        <div class="mt-5 text-center">
          <button
            type="button"
            class="inline-flex items-center justify-center px-4 py-2 font-medium text-red-700 bg-red-100 border border-transparent rounded-md hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:text-sm"
            @click="deleteSelectedGoal(showIsDone)">
            {{ $t("Delete Goal") }}
          </button>
        </div>
      </template>
    </Modal>

    <!-- delete  Plan modal  -->

    <Modal v-model:is-open="delete_plan_modal_is_open" :default-success-msg="$t('Plan Deleted')">
      <template #form="{ done: showIsDone }">
        <button
          class="inset-y-0 right-0 flex items-center justify-center float-right w-6 h-6 ml-5 rounded-full group hover:bg-gray-300"
          type="button"
          @click="delete_plan_modal_is_open = false">
          <X class="w-5 h-5 text-gray-400 group-hover:cursor-pointer" aria-hidden="true" />
        </button>
        <h3 class="text-lg font-medium leading-6 text-gray-900 dark:text-dark-body-text">
          {{ $t("Delete Plan") }}
        </h3>
        <div class="max-w-xl mt-2 text-gray-500 text-md">
          <p>{{ $t("Do you want to delete this plan ?") }}</p>
        </div>
        <div class="mt-5 text-center">
          <button
            type="button"
            class="inline-flex items-center justify-center px-4 py-2 font-medium text-red-700 bg-red-100 border border-transparent rounded-md hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:text-sm"
            @click="deleteSelectedPlan(showIsDone)">
            {{ $t("Delete Plan") }}
          </button>
        </div>
      </template>
    </Modal>

    <Modal v-model:is-open="delete_prospecting_saved_search" :use-toast="true" :default-success-msg="$t('Search Item Deleted')">
      <template #form="{ done: showIsDone }">
        <h3 class="text-lg font-medium leading-6 text-gray-900 dark:text-dark-body-text">
          {{ $t("Delete search item") }}
        </h3>
        <div class="max-w-xl mt-2 text-sm text-gray-500">
          <p>{{ $t("Are you sure you want to delete this search item?") }}</p>
        </div>
        <div class="flex items-center justify-center gap-2 mt-5 text-center">
          <button type="button" class="inline-flex cancel-btn" @click="delete_prospecting_saved_search = false">{{ $t("Cancel") }}</button>

          <button
            type="button"
            class="inline-flex items-center justify-center px-4 py-2 font-medium text-red-700 bg-red-100 border border-transparent rounded-md hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:text-sm"
            @click="
              () => {
                $emitter.emit(ALL_EMITTERS.DELTEE_PROSPECTING_SEARCH, prospecting_search_item_deleted)
                showIsDone()
              }
            ">
            {{ $t("Delete") }}
          </button>
        </div>
      </template>
    </Modal>

    <!-- PROFILE -->

    <Modal v-model:is-open="is_form_updated">
      <template #form="{ close: closeModal }">
        <!-- in case you want to show the done slot you submitted add done: showIsDone, and call showIsDone instead-->
        <div>
          <div class="flex items-center justify-center w-12 h-12 mx-auto bg-green-100 rounded-full">
            <Check class="w-6 h-6 text-green-600" aria-hidden="true" />
          </div>
          <div class="mt-3 text-center sm:mt-5">
            <h3 class="text-lg font-medium leading-6 text-gray-900">
              {{ $t("Profile updated successfully") }}
            </h3>
          </div>
        </div>
        <div class="float-right mt-5 sm:mt-6">
          <button type="button" class="cancel-btn" @click="closeModal">{{ $t("Done") }}</button>
        </div>
      </template>
    </Modal>

    <Modal v-model:is-open="colors_ranges_save">
      <!-- in case you want to show the done slot you submitted add done: showIsDone, and call showIsDone instead-->
      <template #form="{}">
        <div class="flex flex-col items-center justify-center h-full px-4 text-center">
          <div class="flex justify-center">
            <CircleCheck class="w-20 h-20 m-10 text-green-400" aria-hidden="true" />
          </div>
          <h2 class="text-3xl font-extrabold text-common-grey">
            {{ $t("Successful") }}
          </h2>
          <p class="mt-4 text-xl text-common-grey">
            {{ $t("Color ranges saved successfully") }}
          </p>

          <button
            type="button"
            class="inline-flex items-center justify-center px-4 py-2 mt-10 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm group hover:bg-gray-50 focus:outline-none"
            @click="colors_ranges_save = false">
            {{ $t("Close") }}
          </button>
        </div>
      </template>
    </Modal>

    <Modal v-model:is-open="empty_key_records_modal_open" :use-toast="true" :default-success-msg="$t('Key People Cleared')">
      <template #form="{ done: showIsDone }">
        <h3 class="text-lg font-medium leading-6 text-gray-900 dark:text-dark-body-text">
          {{ $t("Delete search item") }}
        </h3>
        <div class="max-w-xl mt-2 text-sm text-gray-500">
          <p>{{ $t("Are you sure you want to empty the key people list? This action cannot be reversed!") }}</p>
        </div>
        <div class="flex items-center justify-center gap-2 mt-5 text-center">
          <button type="button" class="inline-flex cancel-btn" @click="empty_key_records_modal_open = false">{{ $t("Cancel") }}</button>

          <button
            type="button"
            class="inline-flex items-center justify-center px-4 py-2 font-medium text-red-700 bg-red-100 border border-transparent rounded-md hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:text-sm"
            @click="
              () => {
                confirmEmptyKeyRecords()
                showIsDone()
              }
            ">
            {{ $t("Empty List") }}
          </button>
        </div>
      </template>
    </Modal>

    <Modal v-model:is-open="delete_color" :default-success-msg="$t('Color Item Deleted')" :use-toast="true">
      <template #form="{ done: showIsDone }">
        <h3 class="text-lg font-medium leading-6 text-gray-900 dark:text-dark-body-text">
          {{ $t("Delete Color Group") }}
        </h3>
        <div class="max-w-xl mt-2 text-sm text-gray-500">
          <p>{{ $t("Are you sure you want to delete this color group?") }}</p>
        </div>
        <div class="flex items-center justify-center gap-2 mt-5 text-center">
          <button type="button" class="inline-flex cancel-btn" @click="delete_color = false">{{ $t("Cancel") }}</button>

          <button
            type="button"
            class="inline-flex items-center justify-center px-4 py-2 font-medium text-red-700 bg-red-100 border border-transparent rounded-md hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:text-sm"
            @click="
              () => {
                $emitter.emit('confirmDeleteColor', color_deleted, showIsDone)
              }
            ">
            {{ $t("Delete") }}
          </button>
        </div>
      </template>
    </Modal>

    <Modal v-model:is-open="delete_journey" :default-success-msg="$t('Journey Deleted')" :use-toast="true">
      <template #form="{ done: showIsDone }">
        <h3 class="text-lg font-medium leading-6 text-gray-900 dark:text-dark-body-text">
          {{ $t("Delete journey") }}
        </h3>
        <div class="max-w-xl mt-2 text-sm text-gray-500">
          <p>{{ $t("Are you sure you want to delete this journey?") }}</p>
        </div>
        <div class="flex items-center justify-center gap-2 mt-5 text-center">
          <button type="button" class="inline-flex cancel-btn" @click="delete_journey = false">{{ $t("Cancel") }}</button>

          <button
            type="button"
            class="inline-flex items-center justify-center px-4 py-2 font-medium text-red-700 bg-red-100 border border-transparent rounded-md hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:text-sm"
            @click="
              () => {
                $emitter.emit(ALL_EMITTERS.JOURNEY_DELTED, journey_deleted)
                showIsDone()
              }
            ">
            {{ $t("Delete") }}
          </button>
        </div>
      </template>
    </Modal>

    <!-- Metric Settings Modal -->

    <Modal
      v-model:is-open="metric_settings_modal_open"
      :use-toast="true"
      :default-success-msg="$t('Metric updated successfully')"
      @before-close="resetCurrentValues">
      <!-- in case you want to show the done slot you submitted add done: showIsDone, and call showIsDone instead-->
      <template #form="{ close: closeModal, done: showIsDone }">
        <h3 class="text-2xl font-medium leading-10 text-left text-gray-600 dark:text-dark-body-text">
          {{ $t("Edit Metric") }}
        </h3>

        <MetricSettingsForm
          :metric="selected_metric"
          @done="
            () => {
              showIsDone()
            }
          "
          @cancel="closeModal" />
      </template>
    </Modal>

    <!--Custom Metric Edit Modal -->

    <Modal
      v-model:is-open="custom_metric_edit_modal_open"
      :use-toast="true"
      :default-success-msg="$t('Metric updated successfully')"
      @before-close="resetCurrentValues">
      <!-- in case you want to show the done slot you submitted add done: showIsDone, and call showIsDone instead-->
      <template #form="{ close: closeModal, done: showIsDone }">
        <h3 class="text-2xl font-medium leading-10 text-left text-gray-600 dark:text-dark-body-text">
          {{ $t("Edit Metric") }}
        </h3>

        <EditCustomMetricForm
          :metric="selected_metric"
          @done="
            () => {
              showIsDone()
            }
          "
          @cancel="closeModal" />
      </template>
    </Modal>
    <!--Custom Metric Edit Modal -->

    <Modal
      v-model:is-open="computed_metric_edit_modal_open"
      :use-toast="true"
      :default-success-msg="$t('Computed metric updated successfully')"
      max-width="max-w-[1300px]"
      @before-close="resetCurrentValues">
      <!-- in case you want to show the done slot you submitted add done: showIsDone, and call showIsDone instead-->
      <template #form="{ close: closeModal, done: showIsDone }">
        <h3 class="text-2xl font-medium leading-10 text-left text-gray-600 dark:text-dark-body-text">
          {{ $t("Edit Metric") }}
        </h3>

        <EditComputedMetricForm
          :metric="selected_metric"
          @done="
            () => {
              showIsDone()
            }
          "
          @cancel="closeModal" />
      </template>
    </Modal>

    <!-- List Metric Modal -->

    <Modal
      v-model:is-open="list_metric_modal_open"
      :use-toast="true"
      :default-success-msg="$t('Graph created successfully')"
      @before-close="resetCurrentValues">
      <!-- in case you want to show the done slot you submitted add done: showIsDone, and call showIsDone instead-->
      <template #form="{ close: closeModal, done: showIsDone }">
        <h3 class="text-2xl font-medium leading-10 text-left text-gray-600 dark:text-dark-body-text">
          {{ $t("Edit Metric") }}
        </h3>
        <div>{{ $t("Edit") }}</div>

        <ListMetricForm
          :metric="list_metric"
          @done="
            () => {
              showIsDone()
            }
          "
          @cancel="closeModal" />
      </template>
    </Modal>

    <Modal v-model:is-open="module_not_activated_modal">
      <!-- in case you want to show the done slot you submitted add done: showIsDone, and call showIsDone instead-->
      <template #form="{ close: closeModal }">
        <h3 class="flex text-2xl font-medium leading-10 text-left text-gray-600 dark:text-dark-body-text">
          <div class="flex items-center justify-center flex-shrink-0 w-12 h-12 mx-auto bg-red-100 rounded-full sm:mx-0 sm:h-10 sm:w-10">
            <TriangleAlert class="w-6 h-6 text-red-600" aria-hidden="true" />
          </div>
          <span class="px-1"> {{ $t("Module Activation") }} </span>
        </h3>
        <div>
          <p class="m-2 mt-5 text-gray-800 text-md dark:text-white">
            {{ $t("Module") }} {{ not_activated_module }}
            {{ $t("is not Activated please contact your account representative to activate the module!") }}
          </p>
        </div>
        <div class="float-right mt-5 sm:mt-6">
          <button type="button" class="w-20 cancel-btn" @click="closeModal">{{ $t("OK") }}</button>
        </div>
      </template>
    </Modal>

    <!-- Add New Journey -->

    <Modal
      v-model:is-open="add_new_journey_modal_is_open"
      :use-toast="true"
      :default-success-msg="$t('Great! You have successfully added a journey')">
      <template #form="{ close: closeModal, done: showIsDone }">
        <h3 class="text-2xl font-medium leading-10 text-gray-800 dark:text-dark-body-text">
          {{ $t("New Journey") }}
        </h3>
        <NewJourneyForm
          @cancel="closeModal"
          @done="
            () => {
              showIsDone()
            }
          " />
      </template>
    </Modal>

    <!-- Edit Journey -->

    <Modal v-model:is-open="edit_journey_modal_is_open" :use-toast="true" :default-success-msg="$t('Journey successfully updated')">
      <template #form="{ close: closeModal, done: showIsDone }">
        <h3 class="text-2xl font-medium leading-10 text-gray-800 dark:text-dark-body-text">
          {{ $t("Edit Journey") }}
        </h3>
        <EditJourneyForm
          :journey="selected_journey"
          @cancel="closeModal"
          @done="
            () => {
              showIsDone()
            }
          " />
      </template>
    </Modal>
    <!-- Send Email Modal -->

    <Modal
      v-model:is-open="send_email_open"
      :default-success-msg="$t('Email sent successfully')"
      :use-toast="true"
      @before-close="resetCurrentValues">
      <!-- in case you want to show the done slot you submitted add done: showIsDone, and call showIsDone instead-->
      <template #form="{ close: closeModal, done: showIsDone }">
        <h3 class="text-2xl font-medium leading-10 text-left text-gray-600 dark:text-dark-body-text">
          {{ $t("Send Email") }}
        </h3>
        <SendEmailForm
          :count="total_records_count"
          :records="records"
          :is-all-records="is_all_records"
          :excluded-records="excluded_records"
          :cc-records="cc_records"
          :record="record"
          :current-object-type="current_object_type"
          :content="content"
          :ref-record="ref_record"
          :reply-id="reply_id"
          thread-id:
          thread_id
          @done="
            () => {
              showIsDone()
            }
          "
          @cancel="closeModal" />
      </template>
    </Modal>
    <!-- Graph Detail Modal -->

    <Modal v-model:is-open="graph_detail_modal_open">
      <template #form="{ close: closeModal }">
        <!-- <h3 class="text-2xl font-medium leading-10 text-gray-800 dark:text-dark-body-text">
          List of all Records
        </h3> -->
        <GraphDetail
          :metric="graph_rollup_metric"
          :filter="graph_detail_filter"
          :graph-metric="graph_metric"
          :graph-label-value="graph_label_value"
          :truncate-by="truncate_by"
          @cancel="closeModal" />
      </template>
    </Modal>

    <!-- Nested Metric Modal -->

    <Modal v-model:is-open="nested_metric_modal_open">
      <template #form="{ close: closeModal }">
        <!-- <h3 class="text-2xl font-medium leading-10 text-gray-800 dark:text-dark-body-text">
          List of all Records
        </h3> -->
        <NestedMetricView :metric="metric" :record="record" @cancel="closeModal" />
      </template>
    </Modal>

    <!-- Competitor Insights Modal -->

    <Modal v-model:is-open="insights_modal_open">
      <template #form="{ close: closeModal }">
        <div>
          <h3 class="my-2 ml-2 text-xl dark:text-white">
            {{ $t("Insights for") }} {{ competitor.short_name }}
          </h3>
          <CompanyInsightsComponent class="p-4" :competitor="competitor" @cancel="closeModal" />
        </div>
      </template>
    </Modal>

    <Modal
      v-model:is-open="is_delete_object_modal_open"
      :use-toast="true"
      :default-success-msg="`${to_be_deleted_object?.type} ` + $t('Deleted')"
      max-width="max-w-[600px] mx-5">
      <template #form="{ close: closeModal, done: showIsDone }">
        <h3 class="text-lg font-medium leading-6 text-gray-900 dark:text-dark-body-text">
          {{ $t("Delete") }} {{ to_be_deleted_object.type.toLowerCase() }}
          {{ to_be_deleted_object.name }}
        </h3>
        <div class="max-w-xl mt-2 text-sm text-gray-500">
          <p>{{ $t("Once you delete a") }} {{ to_be_deleted_object.type.toLowerCase() }}, {{ $t("you can't restore it") }}.</p>
        </div>
        <div v-if="to_be_deleted_object.type.toLowerCase() == 'plan'" class="mt-5">
          <input
            v-model="confirm_delete_nested_objects"
            type="checkbox"
            class="form-checkbox text-red-500 checked:bg-red-500 bg-light-box-background dark:bg-dark-box-background border-[2px] focus:ring-0 ring-dark/0 border-common-grey w-5 rounded h-5 transition ease-in-out duration-side-menu-transition-duration">
          <span class="mt-5 ml-2 text-sm">{{ $t("Do you want to delete all Goals and Tasks included in this Plan?") }}</span>
        </div>
        <div class="flex flex-row justify-end gap-2 mt-5 text-center">
          <button type="button" class="cancel-btn" @click="closeModal">{{ $t("Cancel") }}</button>
          <button type="button" class="danger-btn w-fit" @click="objectDeleteAction(showIsDone, closeModal)">
            {{ $t("Delete") }} {{ to_be_deleted_object.type }}
          </button>
        </div>
      </template>
    </Modal>

    <Modal
      v-model:is-open="is_move_email_to_trash_modal_open"
      :use-toast="true"
      :default-success-msg="$t('Email moved to trash')"
      max-width="max-w-[600px] mx-5">
      <template #form="{ close: closeModal, done: showIsDone }">
        <h3 class="text-lg font-medium leading-6 text-gray-900 dark:text-dark-body-text">
          {{ $t("Move Email to trash?") }}
        </h3>
        <div class="max-w-xl mt-2 text-sm text-gray-500">
          <p>{{ $t("Email will be moved to trash.") }}</p>
        </div>
        <div class="flex flex-row justify-end gap-2 mt-5 text-center">
          <button type="button" class="cancel-btn" @click="closeModal">{{ $t("Cancel") }}</button>
          <button type="button" class="danger-btn w-fit" @click="objectDeleteAction(showIsDone, closeModal)">
            {{ $t("Move to trash") }}
          </button>
        </div>
      </template>
    </Modal>

    <Modal
      v-model:is-open="is_restore_email_from_trash_modal_open"
      :use-toast="true"
      :default-success-msg="$t('Email restored')"
      max-width="max-w-[600px] mx-5">
      <template #form="{ close: closeModal, done: showIsDone }">
        <h3 class="text-lg font-medium leading-6 text-gray-900 dark:text-dark-body-text">
          {{ $t("Restore email") }}?
        </h3>
        <div class="max-w-xl mt-2 text-sm text-gray-500">
          <p>{{ $t("Are you sure you want to restore this email?") }}</p>
        </div>
        <div class="flex flex-row justify-end gap-2 mt-5 text-center">
          <button type="button" class="cancel-btn" @click="closeModal">{{ $t("Cancel") }}</button>
          <button type="button" class="danger-btn w-fit" @click="objectDeleteAction(showIsDone, closeModal)">
            {{ $t("Restore email") }}
          </button>
        </div>
      </template>
    </Modal>

    <Modal
      v-model:is-open="is_share_object_open"
      :use-toast="true"
      :default-success-msg="`${to_be_shared_object?.type} ` + $t('workspaces shared list updated')">
      <template #form="{ close: closeModal, done: showIsDone }">
        <h1 class="flex items-center space-x-4 text-2xl font-medium leading-6 text-gray-900 dark:text-dark-body-text">
          <Share2 class="mr-1" /> {{ $t("Share to...") }}
        </h1>
        <div class="my-4 text-gray-500">
          <p>{{ $t("Share") }} {{ to_be_shared_object.name }} {{ $t("to a workspace") }}</p>
        </div>
        <AsyncAutoCompleteInput
          :add="true"
          :add-label="$t('Share to Workspace')"
          :clear-on-select="true"
          :is-box-background="true"
          :selected-items="workspaces_selected"
          classes="w-full"
          input-classes="appearance-none text-left block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm "
          name="workspace"
          value-attr="id"
          search-field="name"
          filter-field="record"
          :filter-field-value="recordIdToSendToWorkspaceSelection(to_be_shared_object)"
          :endpoint="`/api/organizations/${$store.state.organization.id}/workspaces`"
          type="text"
          :placeholder="$t('Search and add item to workspace')"
          @chosen="
            ($event) => {
              workspaces_selected.push($event)
            }
          " />
        <div v-for="(workspace_, index) in workspaces_selected" :key="workspace_.id" class="flex items-center mt-3 space-x-2">
          <img v-if="workspace_.logo" class="inline-block rounded-full h-9 w-9 object-cover" :src="workspace_.logo" alt="">
          <Avatar v-else :radius="34" :hover-effect="true" :first-name="workspace_.name" />
          <div>{{ workspace_.name }}</div>
          <div @click="workspaces_selected.splice(index, 1)">
            <Trash2 class="w-5 h-5 mr-2 text-gray-300 cursor-pointer" />
          </div>
        </div>
        <!-- <AsyncMultiSelectWorkspaceInput
          add-label="workspaces"
          :clear-on-select="true"
          :is-box-background="true"
          :selected-workspaces="to_be_shared_object.workspaces_shared_in"
          :record="recordIdToSendToWorkspaceSelection(to_be_shared_object)"
          classes="w-full"
          input-classes="appearance-none text-left block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm "
          name="workspaces"
          value-attr="id"
          :endpoint="`/api/organizations/${$store.state.organization.id}/workspaces`"
          endpoint-method="get"
          search-field="name"
          type="text"
          placeholder="Workspaces"
          @update:currentItems="
            ($event) => {
              workspaces_selected = $event
            }
          "
        /> -->
        <div class="flex flex-row justify-between gap-2 mt-5 text-center">
          <button type="button" class="cancel-btn" @click="closeModal">{{ $t("Cancel") }}</button>
          <button type="button" class="primary-btn w-fit" @click="updateWorkspacesList(showIsDone, closeModal)">{{ $t("Save") }}</button>
        </div>
      </template>
    </Modal>

    <!-- Add Goal taksk to plan -->

    <Modal v-model:is-open="add_goal_tasks_to_plan_modal" :default-success-msg="$t('Added Goal Tasks to Plan')" :use-toast="true">
      <template #form="{ close: closeModal, done: showIsDone }">
        <h3 class="text-2xl font-medium leading-10 text-gray-800 dark:text-dark-body-text">
          {{ $t("Add Goal tasks to Plan") }}
        </h3>
        <div>
          {{ $t("Goal(s) added") }} {{ multiple_goals ? $t("have") : $t("has") }} {{ tasks_count }} task{{ tasks_count > 1 ? "s" : "" }}.
          {{ $t("Would you like to add") }} {{ tasks_count > 1 ? $t("these") : $t("this") }} {{ $t("task(s)") }}
          {{ $t("to the plan?") }}
        </div>
        <div class="flex w-full mt-10">
          <button type="button" class="w-full mr-4 btn primary-btn" @click="handleAddGoalTasksToPlan(closeModal, showIsDone)">
            {{ $t("Add tasks to plan") }}
          </button>
          <button type="button" class="w-full btn cancel-btn" @click="closeModal">{{ $t("Don't add tasks to plan") }}</button>
        </div>
      </template>
    </Modal>

    <Modal v-model:is-open="is_phase_modal_open" :default-success-msg="$t('New Phase successfully created')" :use-toast="true">
      <template #form="{ done: showIsDone, close: closeModal }">
        <h3 class="text-2xl font-medium leading-10 text-center text-gray-800 dark:text-white">
          {{ $t("New Phase") }}
        </h3>
        <NewPhaseForm
          :phases-journeys="journeys"
          :journey="selected_journey"
          @cancel="closeModal"
          @done="
            (phase) => {
              $emitter.emit(ALL_EMITTERS.PHASES_ADDED_TO_JOURNEYS, phase)
              $emitter.emit(ALL_EMITTERS.PHASES_ADDED_TO_CUSTOMER_JOURNEYS_VIEW, phase)
              showIsDone()
            }
          " />
      </template>
    </Modal>

    <Modal v-model:is-open="is_phase_edit_modal_open" :default-success-msg="$t('Phase successfully modified')" :use-toast="true">
      <template #form="{ close: closeModal, done: showIsDone }">
        <h3 class="text-2xl font-medium leading-10 text-center text-gray-800 dark:text-white">
          {{ $t("Edit") }} "{{ phase?.name }}" {{ $t("Phase") }}
        </h3>
        <EditPhaseForm
          :phases-journeys="journeys"
          :phase="phase"
          :journey="selected_journey"
          @cancel="closeModal"
          @done="
            (phase) => {
              $emitter.emit(ALL_EMITTERS.PHASE_MODIFIED_IN_JOURNEYS, phase)
              $emitter.emit(ALL_EMITTERS.PHASE_MODIFIED_IN_CUSTOMER_JOURNEYS_VIEW, phase)
              showIsDone()
            }
          " />
      </template>
    </Modal>

    <Modal v-model:is-open="is_delete_phase_modal_open" :default-success-msg="$t('Phase Deleted')" :use-toast="true">
      <template #form="{ close: closeModal, done: showIsDone }">
        <h3 class="text-lg font-medium leading-6 text-common-grey">
          {{ $t("Delete phase") }} {{ phase.name }}
        </h3>
        <div class="max-w-xl mt-2 text-sm text-common-grey">
          <p>{{ $t("Once you delete a phase, you can't restore it.") }}</p>
        </div>
        <div class="mt-5 text-center">
          <button
            type="button"
            class="inline-flex items-center justify-center px-4 py-2 font-medium text-red-700 bg-red-100 border border-transparent rounded-md hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:text-sm"
            @click="removePhase(showIsDone)">
            {{ $t("Delete Phase") }}
          </button>
          <button
            type="button"
            class="inline-flex items-center justify-center px-4 py-2 ml-2 font-medium border rounded-md dark:text-white dark:border-dark-divider-border focus:outline-none focus:ring-2 focus:ring-offset-2 sm:text-sm"
            @click="closeModal">
            {{ $t("Cancel") }}
          </button>
        </div>
      </template>
    </Modal>

    <Modal v-model:is-open="add_new_component_modal" :default-success-msg="$t('Phase Deleted')" :use-toast="true">
      <template #form="{ close: closeModal, done: showIsDone }">
        <AddNewComponent
          :page-reference="page_reference"
          :current-template="current_template"
          :initial-step="new_layout_component_initial_step"
          :initial-selected-component="new_layout_component_selected_component"
          @cancel="closeModal"
          @done="showIsDone" />
      </template>
    </Modal>

    <Modal v-model:is-open="delete_workspace_team_member_modal_open" :default-success-msg="$t('Team member removed')" :use-toast="true">
      <template #form="{ close: closeModal, done: showIsDone }">
        <h3 class="text-lg font-medium leading-6">
          {{ $t("Remove team member") }}
        </h3>
        <div class="max-w-xl mt-2 text-sm text-common-grey">
          <p>{{ $t("Are you sure you want to remove this team member?") }}</p>
        </div>
        <div class="mt-5 text-center">
          <button
            type="button"
            class="inline-flex items-center justify-center px-4 py-2 font-medium text-red-700 bg-red-100 border border-transparent rounded-md hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:text-sm"
            @click="removeWorkspaceTeamMember(showIsDone)">
            {{ $t("Remove") }}
          </button>
          <button
            type="button"
            class="inline-flex items-center justify-center px-4 py-2 ml-2 font-medium border rounded-md dark:text-white dark:border-dark-divider-border focus:outline-none focus:ring-2 focus:ring-offset-2 sm:text-sm"
            @click="closeModal">
            {{ $t("Cancel") }}
          </button>
        </div>
      </template>
    </Modal>

    <Modal v-model:is-open="delete_workspace_contact_modal_open" :default-success-msg="$t('Contact removed')" :use-toast="true">
      <template #form="{ close: closeModal, done: showIsDone }">
        <h3 class="text-lg font-medium leading-6">
          {{ $t("Remove contact") }}
        </h3>
        <div class="max-w-xl mt-2 text-sm text-common-grey">
          <p>{{ $t("Are you sure you want to remove this contact?") }}</p>
        </div>
        <div class="mt-5 text-center">
          <button
            type="button"
            class="inline-flex items-center justify-center px-4 py-2 font-medium text-red-700 bg-red-100 border border-transparent rounded-md hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:text-sm"
            @click="removeWorkspaceContact(showIsDone)">
            {{ $t("Remove") }}
          </button>
          <button
            type="button"
            class="inline-flex items-center justify-center px-4 py-2 ml-2 font-medium border rounded-md dark:text-white dark:border-dark-divider-border focus:outline-none focus:ring-2 focus:ring-offset-2 sm:text-sm"
            @click="closeModal">
            {{ $t("Cancel") }}
          </button>
        </div>
      </template>
    </Modal>

    <Modal v-model:is-open="delete_workspace_page_modal_open" :default-success-msg="$t('Page Removed')" :use-toast="true">
      <template #form="{ close: closeModal, done: showIsDone }">
        <h3 class="text-lg font-medium leading-6">
          {{ $t("Remove Page") }}
        </h3>
        <div class="max-w-xl mt-2 text-sm text-common-grey">
          <p>{{ $t("Are you sure you want to remove this page?") }}</p>
        </div>
        <div class="mt-5 text-center">
          <button
            type="button"
            class="inline-flex items-center justify-center px-4 py-2 font-medium text-red-700 bg-red-100 border border-transparent rounded-md hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:text-sm"
            @click="removeWorkspacePage(showIsDone)">
            {{ $t("Remove") }}
          </button>
          <button
            type="button"
            class="inline-flex items-center justify-center px-4 py-2 ml-2 font-medium border rounded-md dark:text-white dark:border-dark-divider-border focus:outline-none focus:ring-2 focus:ring-offset-2 sm:text-sm"
            @click="closeModal">
            {{ $t("Cancel") }}
          </button>
        </div>
      </template>
    </Modal>

    <Modal v-model:is-open="new_workspace_modal_open" :default-success-msg="$t('Workspace Created')" :use-toast="true">
      <template #form="{ close: closeModal, done: showIsDone }">
        <NewWorkspaceForm @cancel="closeModal" @done="showIsDone" />
      </template>
    </Modal>

    <Modal
      v-model:is-open="add_new_workspace_contact_collaborator_modal_open"
      :default-success-msg="$t('Workspace Collaborator Added')"
      :use-toast="true">
      <template #form="{ close: closeModal, done: showIsDone }">
        <button
          class="inset-y-0 right-0 flex items-center justify-center float-right w-6 h-6 ml-5 rounded-full group hover:bg-gray-300"
          type="button"
          @click="add_new_workspace_contact_collaborator_modal_open = false">
          <X class="w-5 h-5 text-gray-400 group-hover:cursor-pointer" aria-hidden="true" />
        </button>
        <h3 class="text-lg font-medium leading-6 text-gray-900 dark:text-dark-body-text">
          {{ $t("Add New Collaborator") }}
        </h3>
        <WorkspaceContactCollaboratorForm :workspace="workspace" @cancel="closeModal" @done="showIsDone" />
      </template>
    </Modal>

    <Modal
      v-model:is-open="add_new_workspace_team_collaborator_modal_open"
      :default-success-msg="$t('Workspace Team Collaborator Added')"
      :use-toast="true">
      <template #form="{ close: closeModal, done: showIsDone }">
        <button
          class="inset-y-0 right-0 flex items-center justify-center float-right w-6 h-6 ml-5 rounded-full group hover:bg-gray-300"
          type="button"
          @click="add_new_workspace_team_collaborator_modal_open = false">
          <X class="w-5 h-5 text-gray-400 group-hover:cursor-pointer" aria-hidden="true" />
        </button>
        <h3 class="text-lg font-medium leading-6 text-gray-900 dark:text-dark-body-text">
          {{ $t("Add New Team Collaborator") }}
        </h3>
        <WorkspaceTeamCollaboratorForm :workspace="workspace" @cancel="closeModal" @done="showIsDone" />
      </template>
    </Modal>

    <Modal v-model:is-open="edit_note_modal" :default-success-msg="$t('Note Modified')" :use-toast="true">
      <template #form="{ close: closeModal, done: showIsDone }">
        <EditTextForm
          :id="note_id"
          :label="label"
          :description="description"
          :background-color="background_color"
          @cancel="closeModal"
          @done="showIsDone" />
      </template>
    </Modal>

    <Modal v-model:is-open="change_workspaces_organization_modal_open" :default-success-msg="$t('Organization Changed')" :use-toast="true">
      <template #form="{ close: closeModal, done: showIsDone }">
        <h3 class="pb-3 mb-6 text-lg font-medium leading-6 text-gray-900 border-b dark:text-dark-body-text">
          {{ $t("Change Organization") }}
        </h3>
        <ChangeWorkspaceOrganizationForm @cancel="closeModal" @done="showIsDone" />
      </template>
    </Modal>

    <Modal v-model:is-open="change_organization_modal_open" :default-success-msg="$t('Organization Changed')" :use-toast="true">
      <template #form="{ close: closeModal, done: showIsDone }">
        <h3 class="pb-3 mb-6 text-lg font-medium leading-6 text-gray-900 border-b dark:text-dark-body-text">
          {{ $t("Add or Join Organization") }}
        </h3>
        <JoinOrganizationForm @cancel="closeModal" @done="showIsDone" />
      </template>
    </Modal>

    <!-- Edit Key Record  -->

    <Modal
      v-model:is-open="edit_key_records_modal_open"
      max-width="max-w-[500px] mx-5"
      :default-success-msg="$t('Key People Changed')"
      :use-toast="true">
      <template #form="{ close: closeModal, done: showIsDone }">
        <h3 class="pb-3 mb-6 text-lg font-medium leading-6 text-gray-900 border-b dark:text-dark-body-text">
          {{ $t("Change Key People") }}
        </h3>
        <ChangeKeyRecordsForm :record="record" @cancel="closeModal" @done="showIsDone" />
      </template>
    </Modal>

    <!-- delete  Interaction modal  -->

    <Modal
      v-model:is-open="delete_workspaces_modal_open"
      :use-toast="true"
      :default-success-msg="workspaces_to_delete.length > 1 ? $t('Workspaces Deleted') : $t('Workspace Deleted')">
      <template #form="{ done: showIsDone }">
        <button
          class="inset-y-0 right-0 flex items-center justify-center float-right w-6 h-6 ml-5 rounded-full group hover:bg-gray-300"
          type="button"
          @click="delete_workspaces_modal_open = false">
          <X class="w-5 h-5 text-gray-400 group-hover:cursor-pointer" aria-hidden="true" />
        </button>
        <h3 class="text-lg font-medium leading-6 text-gray-900 dark:text-dark-body-text">
          {{ $t("Delete Workspace(s)") }}
        </h3>
        <div class="mt-2 text-gray-500 text-md">
          <p>
            {{ $t("You are permanently deleting the") }}
            {{ workspaces_to_delete.length > 1 ? $t("selected workspaces") : $t("selected workspace") }}
            . {{ $t("This action cannot be undone. Are you sure you want to delete?") }}
          </p>
        </div>

        <div class="mt-5">
          <span class="mt-5 mr-2">{{ $t("I understand") }}</span>
          <input
            v-model="confirm_delete_workspaces_checkbox"
            type="checkbox"
            class="form-checkbox text-red-500 checked:bg-red-500 bg-light-box-background dark:bg-dark-box-background border-[2px] focus:ring-0 ring-dark/0 border-common-grey w-5 rounded h-5 transition ease-in-out duration-side-menu-transition-duration">
        </div>

        <div class="mt-5 text-center">
          <button
            :disabled="!confirm_delete_workspaces_checkbox"
            type="button"
            :class="[
              {
                'text-red-700 bg-red-100 hover:bg-red-200 focus:ring-red-500': confirm_delete_workspaces_checkbox
              },
              {
                'text-gray-300 bg-gray-100 hover:bg-gray-200 focus:ring-gray-300': !confirm_delete_workspaces_checkbox
              }
            ]"
            class="inline-flex items-center justify-center px-4 py-2 font-medium border border-transparent rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 sm:text-sm"
            @click="deleteSelectedWorkspace(showIsDone)">
            {{ $t("Delete Workspace") }}
          </button>
          <button
            type="button"
            class="inline-flex items-center justify-center px-4 py-2 ml-2 font-medium border rounded-md dark:text-white dark:border-dark-divider-border focus:outline-none focus:ring-2 focus:ring-offset-2 sm:text-sm"
            @click="delete_workspaces_modal_open = false">
            {{ $t("Cancel") }}
          </button>
        </div>
      </template>
    </Modal>

    <!-- Edit Key Record  -->

    <Modal v-model:is-open="page_settings_modal_open" :default-success-msg="$t('Menu changes applied')" :use-toast="true">
      <template #form="{ close: closeModal, done: showIsDone }">
        <PageSettings :disable-grouping="disable_pages_grouping" :current-item="current_item" @cancel="closeModal" @done="showIsDone" />
      </template>
    </Modal>

    <!-- Add new record -->

    <Modal v-model:is-open="add_new_record_modal_open" :default-success-msg="$t('New record added')" :use-toast="true">
      <template #form="{ close: closeModal, done: showIsDone }">
        <AddNewRecordModal @cancel="closeModal" @done="showIsDone" />
      </template>
    </Modal>

    <!-- Templates -->

    <Modal v-model:is-open="add_new_template_modal_open" :default-success-msg="$t('New record added')" :use-toast="true">
      <template #form="{ close: closeModal, done: showIsDone }">
        <AddTemplatesModal @cancel="closeModal" @done="showIsDone" />
      </template>
    </Modal>

    <!-- Set Up Forecasting Object  -->

    <Modal v-model:is-open="setup_forecasting_modal_open" :default-success-msg="$t('Forecasting Preference saved')" :use-toast="true">
      <template #form="{ close: closeModal, done: showIsDone }">
        <SetupForecastingModal @cancel="closeModal" @done="showIsDone" />
      </template>
    </Modal>

    <!-- Find leads locked  -->

    <Modal
      v-model:is-open="find_leads_locked_modal_open"
      :default-success-msg="$t('Menu changes applied')"
      max-width="w-[1000px] !rounded-xl !m-0 !p-0"
      :use-toast="true">
      <template #form="{ close: closeModal, done: showIsDone }">
        <FindLeadsLocked @cancel="closeModal" @done="showIsDone" />
      </template>
    </Modal>

    <!-- Share activities -->

    <Modal
      v-model:is-open="share_activities_modal_is_open"
      max-width="max-w-[750px]"
      :use-toast="true"
      :default-success-msg="$t('You have successfully shared activity')">
      <template #form="{ close: closeModal, done: showIsDone, error: showError }">
        <ShareActivities :object="object" @error="showError" @done="showIsDone" @cancel="closeModal" />
      </template>
      <template #error="{ close: closeModal }">
        <div class="flex flex-col items-center justify-center h-full px-4 text-center">
          <div class="flex justify-center">
            <CircleAlert class="w-20 h-20 m-10 text-red-400" aria-hidden="true" />
          </div>
          <h2 class="text-3xl font-extrabold text-gray-900 dark:text-dark-body-text">
            {{ $t("Error") }}
          </h2>
          <p class="mt-4 text-xl text-gray-500">
            {{ $t("Error sharing an activity, please try again!") }}
          </p>
          <button
            type="button"
            class="inline-flex items-center justify-center px-4 py-2 mt-10 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm group dark:text-dark-body-text dark:bg-dark-box-background dark:border-dark-divider-border hover:bg-gray-50 focus:outline-none"
            @click="closeModal">
            {{ $t("Close") }}
          </button>
        </div>
      </template>
    </Modal>
    <!-- Invite Teammates -->

    <Modal
      v-model:is-open="invite_teammates_modal_is_open"
      max-width="max-w-[950px] !p-0 !m-0"
      :use-toast="true"
      :default-success-msg="$t('You have successfully invited teammates')">
      <template #form="{ close: closeModal, done: showIsDone, error: showError }">
        <InviteTeammates @error="showError" @done="showIsDone" @cancel="closeModal" />
      </template>
      <template #error="{ close: closeModal }">
        <div class="flex flex-col items-center justify-center h-full p-4 text-center">
          <div class="flex justify-center">
            <CircleAlert class="w-20 h-20 m-10 text-red-400" aria-hidden="true" />
          </div>
          <h2 class="text-3xl font-extrabold text-gray-900 dark:text-dark-body-text">
            {{ $t("Error") }}
          </h2>
          <p class="mt-4 text-xl text-gray-500">
            {{ $t("Error inviting teammates, please try again!") }}
          </p>
          <button
            type="button"
            class="inline-flex items-center justify-center px-4 py-2 mt-10 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm group dark:text-dark-body-text dark:bg-dark-box-background dark:border-dark-divider-border hover:bg-gray-50 focus:outline-none"
            @click="closeModal">
            {{ $t("Close") }}
          </button>
        </div>
      </template>
    </Modal>
  </teleport>
</template>

<script>
import AutoCompleteInput from "@/components/shared/AutoCompleteInput.vue"
import MetricFieldInput from "@/components/shared/MetricFieldInput.vue"
import ComboBoxInput from "@/components/shared/ComboBoxInput.vue"
import SelectInput from "@/components/shared/SelectInput.vue"

import { Record } from "@/models/record"
import Modal from "@/components/shared/Modal.vue"
import NewExternalScore from "@/components/forms/NewExternalScore.vue"
import SurveyForm from "@/components/forms/SurveyForm.vue"
import SurveySendForm from "@/components/forms/SurveySendForm.vue"

import SurveySelectionForm from "@/components/forms/SurveySelectionForm.vue"
import SurveyResponses from "@/components/forms/SurveyResponses.vue"

import Drawer from "@/components/shared/Drawer.vue"
import DrawerFixedFooter from "@/components/shared/DrawerFixedFooter.vue"
import GraphUpdateForm from "@/components/forms/GraphUpdateForm.vue"
import GraphCreateForm from "@/components/forms/GraphCreateForm.vue"
import ForecastNotes from "@/components/shared/ForecastNotes.vue"
import RecordNotes from "@/components/shared/RecordNotes.vue"
import { X, Check, Trash2, CircleCheck, Info, TriangleAlert, CircleAlert, Settings2, Share2 } from "lucide-vue-next"
import MultiSelectInputFlat from "@/components/shared/MultiSelectInputFlat.vue"
import dataService from "@/services/data.service"
//Forms
import SendEmailForm from "@/components/forms/email/SendEmailForm.vue"
import NewMetricForm from "@/components/forms/NewMetricForm.vue"
import FeedbackForm from "@/components/forms/FeedbackForm.vue"
import UpdateInteractionForm from "@/components/forms/UpdateInteractionForm.vue"
import UpdateTaskForm from "@/components/forms/UpdateTaskForm.vue"
import UpdateGoalForm from "@/components/forms/UpdateGoalForm.vue"
import CreateNewInteraction from "@/components/forms/CreateNewInteraction.vue"
import NewGroupForm from "@/components/forms/NewGroupForm.vue"
import UpdateGroupForm from "@/components/forms/UpdateGroupForm.vue"
import NewGoalForm from "@/components/forms/NewGoalForm.vue"
import SharePage from "@/components/forms/SharePage.vue"
import ClonePage from "@/components/forms/ClonePage.vue"
import AssignNewOwner from "@/components/forms/AssignNewOwner.vue"
import AssignCoOwners from "@/components/forms/AssignCoOwners.vue"
import assignTags from "@/components/forms/assignTags.vue"
import CreateNewPlaybook from "@/components/forms/CreateNewPlaybook.vue"
import CreateNewGroup from "@/components/forms/CreateNewGroup.vue"

import CreateNewTask from "@/components/forms/CreateNewTask.vue"
import NewExpansionList from "@/components/forms/NewExpansionList.vue"
import CreatePlan from "@/components/forms/CreatePlan"
import MetricSettingsForm from "@/components/forms/MetricSettingsForm.vue"
import EditCustomMetricForm from "@/components/forms/EditCustomMetricForm.vue"
import EditComputedMetricForm from "@/views/dashboard/admin/metrics/MetricForm.vue"

import EditTextForm from "@/components/forms/EditTextForm.vue"
import ChangeWorkspaceOrganizationForm from "@/components/forms/ChangeWorkspaceOrganizationForm.vue"

import ListMetricForm from "@/components/forms/ListMetricForm.vue"
import EditPlan from "@/components/forms/EditPlan.vue"

import DualList from "@/components/shared/DualList.vue"

import NewJourneyForm from "@/components/forms/NewJourneyForm.vue"
import Avatar from "@/components/shared/Avatar.vue"

import EditJourneyForm from "@/components/forms/EditJourneyForm.vue"

import GraphDetail from "@/components/shared/GraphDetail.vue"
import NestedMetricView from "@/components/shared/NestedMetricView.vue"

import CompanyInsightsComponent from "@/components/shared/CompanyInsightsComponent.vue"
import { utils } from "@/store/helpers"

import AsyncAutoCompleteInput from "@/components/shared/AsyncAutoCompleteInput.vue"

import NewPhaseForm from "@/components/forms/NewPhaseForm.vue"
import EditPhaseForm from "@/components/forms/EditPhaseForm.vue"

import AddNewComponent from "@/components/shared/add-new-component"

import NewWorkspaceForm from "@/components/forms/NewWorkspaceForm.vue"

import WorkspaceContactCollaboratorForm from "@/components/forms/WorkspaceContactCollaboratorForm.vue"
import WorkspaceTeamCollaboratorForm from "@/components/forms/WorkspaceTeamCollaboratorForm.vue"

import ChangeKeyRecordsForm from "@/components/forms/ChangeKeyRecordsForm.vue"
import PageSettings from "@/components/forms/PageSettings.vue"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"
import LongArrowIcon from "@/components/shared/icons/LongArrowIcon.vue"
import MapFieldsIcon from "@/components/shared/icons/MapFieldsIcon.vue"
import AddNewRecordModal from "@/components/forms/AddNewRecordModal.vue"
import AddTemplatesModal from "@/components/forms/AddTemplatesModal.vue"
import SetupForecastingModal from "@/components/forms/SetupForecastingModal.vue"
import FindLeadsLocked from "@/components/shared/FindLeadsLocked.vue"
import ShareActivities from "@/components/forms/ShareActivities.vue"
import InviteTeammates from "@/components/forms/InviteTeammates.vue"

import JoinOrganizationForm from "@/views/onboarding/JoinOrganizationForm.vue"
import { getAuthUrl } from "@/views/dashboard/admin/integrations/oauth.js"
import IntegrationService from "@/services/integration.service"

export default {
  components: {
    JoinOrganizationForm,
    AddNewRecordModal,
    AddTemplatesModal,
    SetupForecastingModal,
    FindLeadsLocked,
    ShareActivities,
    InviteTeammates,
    SelectInput,
    MetricFieldInput,
    ComboBoxInput,
    MapFieldsIcon,
    LongArrowIcon,
    AutoCompleteInput,
    Settings2,
    Avatar,
    PageSettings,
    Share2,
    AsyncAutoCompleteInput,
    NewExternalScore,
    SurveyForm,
    SurveySendForm,
    SurveySelectionForm,
    SurveyResponses,
    SharePage,
    NewWorkspaceForm,
    ClonePage,
    CreatePlan,
    SendEmailForm,
    MultiSelectInputFlat,
    Modal,
    ForecastNotes,
    RecordNotes,
    Drawer,
    DrawerFixedFooter,
    GraphUpdateForm,
    GraphCreateForm,
    NewPhaseForm,
    EditPhaseForm,
    NewMetricForm,
    FeedbackForm,
    UpdateInteractionForm,
    UpdateTaskForm,
    UpdateGoalForm,
    MetricSettingsForm,
    EditTextForm,
    ChangeWorkspaceOrganizationForm,
    EditCustomMetricForm,
    EditComputedMetricForm,
    NewGroupForm,
    UpdateGroupForm,
    NewGoalForm,
    AssignNewOwner,
    AssignCoOwners,
    assignTags,
    CreateNewPlaybook,
    CreateNewGroup,
    CreateNewTask,
    CreateNewInteraction,
    NewExpansionList,
    Info,
    Check,
    CircleCheck,
    CircleAlert,
    TriangleAlert,
    X,
    ListMetricForm,
    EditPlan,
    DualList,
    NewJourneyForm,
    EditJourneyForm,
    GraphDetail,
    NestedMetricView,
    CompanyInsightsComponent,
    AddNewComponent,
    WorkspaceContactCollaboratorForm,
    WorkspaceTeamCollaboratorForm,
    ChangeKeyRecordsForm,
    Trash2
  },
  inject: ["getPageTabs", "getPageGraphs", "getViewMetrics", "setPageTabs", "setPageGraphs", "setViewMetrics"],
  data() {
    return {
      ALL_EMITTERS,
      Record,
      closeCallBack: () => {},
      confirm_delete_nested_objects: false,
      is_share_object_open: false,
      to_be_shared_object: null,
      to_be_shared_with_end_users: [],
      workspaces_selected: [],
      selected_contact: null,

      //
      to_be_deleted_object: null,
      is_delete_object_modal_open: false,
      objectDeleteAction: () => {},

      // On boarding Modals
      module_not_activated_modal: false,
      not_activated_module: null,
      is_onboarding_modal_open: false,
      hide_onboarding_modal: false,

      // nps score
      is_external_score_modal_open: false,
      // Graphs
      is_create_graph_modal_open: false,
      is_delete_graph_modal_open: false,
      is_update_graph_modal_open: false,
      is_graphs_modal_open: false,
      updated_graphs: [],
      current_graph: null,

      //Globals
      total_records_count: null,
      current_filter: null,
      records: [],
      is_all_records: false,
      excluded_records: [],
      is_leads_records: null,
      cc_records: [],
      full_records: null,
      record: null,
      group: null,
      goals: [],
      template: null,
      playbook: null,
      playbooks: [],

      generic_delete_modal_open: false,
      generic_delete_item: null,

      // All Metrics
      is_update_table_metrics_modal_open: false,
      is_rollup_metrics_modal_open: false,
      is_created_custom_metric_modal_open: false,
      updated_table_metrics: [],
      updated_rollup_metrics: [],
      component_reference: null,
      layout_page_reference: null,

      //Tabs
      is_update_tabs_modal_open: false,
      updated_tabs: [],

      //Interactions
      current_interaction: null,
      update_interaction_modal_is_open: false,
      new_interaction_modal_is_open: false,
      delete_interaction_modal_is_open: false,

      //Groups
      new_group_modal_is_open: false,
      show_delete_group_confirmation_modal: false,
      show_delete_template_confirmation_modal: false,
      show_edit_group_confirmation_modal: false,

      //Goals
      current_goal: null,
      update_goal_modal_is_open: false,
      new_goal_modal_is_open: false,
      delete_goal_modal_is_open: false,

      //Plans
      current_plan: null,
      update_plan_modal_is_open: false,
      new_plan_modal_is_open: false,
      delete_plan_modal_is_open: false,
      plan_values: null,
      from_expand: false,
      is_template: false,
      in_playbook: false,
      selected_template: null,
      is_edit_template: false,

      //Tasks
      new_task_defaults: {},
      current_task: false,
      update_task_modal_is_open: false,
      new_task_modal_is_open: false,
      delete_task_modal_is_open: false,

      //Owner
      new_owner_modal_is_open: false,
      assign_tags_modal_is_open: false,
      create_playbook_modal_is_open: false,
      create_group_modal_is_open: false,

      //Co-owner
      co_owner_modal_is_open: false,

      //List
      expansion_list: null,
      add_to_expansion_list_modal_is_open: false,
      is_remove_from_expansion_list_modal_open: false,
      is_delete_expansion_list_modal_open: false,

      //Company
      add_company_modal_is_open: false,

      //Settings
      is_form_updated: false,
      //Prospecting
      delete_prospecting_saved_search: false,
      prospecting_search_item_deleted: null,

      //COLORS RANGES
      colors_ranges_save: false,

      //Metric Settings
      selected_metric: null,
      metric_settings_modal_open: false,

      //Custom Metric
      custom_metric_edit_modal_open: false,
      // computed
      computed_metric_edit_modal_open: false,

      show_bulk_records_metric_update_model: false,
      merge_records_metrics_model: false,
      merge_metric_a: null,
      merge_metric_b: null,
      merge_metrics_priority: "none_empty_value",
      target_bulk_update_metric: null,
      target_bulk_update_metric_value: null,
      //List metric
      list_metric_modal_open: false,
      list_metric: null,
      in_plan: false,
      in_goal: false,
      in_interaction: false,
      in_milestone: false,
      is_invite_team_members_modal_open: false,
      show_delete_records_confirmation_modal: false,

      // Global new record
      new_record_object_type: false,
      show_add_new_record: false,

      //Journey
      add_new_journey_modal_is_open: false,
      edit_journey_modal_is_open: false,
      selected_journey: null,
      delete_journey: false,
      journey_deleted: null,

      delete_color: false,
      color_deleted: null,
      // pages tabs and views metrics
      current_page_reference: null,
      first_portfolio_tab: null,
      current_object_type: null,
      current_view_type: null,
      type: null,

      graph_detail_modal_open: false,
      graph_detail_filter: "",
      graph_label_value: null,
      graph_metric: null,
      graph_rollup_metric: null,
      truncate_by: null,

      nested_metric_modal_open: false,
      metric: null,

      //Send Email
      send_email_open: false,
      ref_record: null,
      content: null,
      reply_id: null,
      thread_id: null,

      //insights
      insights_modal_open: false,
      competitor: null,

      //success modal
      success_modal_open: false,
      success_modal_message: "",

      plan_modal_width: "xsm:max-w-[900px] mx-5",
      task_modal_width: "xsm:max-w-[900px] mx-5",
      goal_modal_width: "xsm:max-w-[900px] mx-5",
      interaction_modal_width: "xsm:max-w-[900px] mx-5",

      //
      integration_missing_modal: false,
      integration_modal_message: "",

      // plan goal tasks add
      add_goal_tasks_to_plan_modal: false,
      tasks_count: 0,
      multiple_goals: false,
      goals_with_tasks: [],

      //phase create
      is_phase_modal_open: false,
      journeys: null,

      //phase edit
      is_phase_edit_modal_open: false,
      phase: null,

      //delete phase
      is_delete_phase_modal_open: false,

      //Leads
      updated_leads_table_metrics: [],
      updated_activities_table_headers: [],
      is_update_leads_table_modal_open: false,
      is_update_activities_table_modal_open: false,

      // forecast
      updated_forecast_table_metrics: [],
      is_update_forecast_table_modal_open: false,
      is_add_notes_forecasting_modal_open: false,
      is_add_notes_record_modal_open: false,
      current_forecast_name: null,
      forecast_id: null,

      is_move_email_to_trash_modal_open: false,
      to_be_deleted_email: null,

      is_restore_email_from_trash_modal_open: false,
      to_be_restored_email: null,

      //New component
      current_template: null,
      add_new_component_modal: false,
      new_layout_component_initial_step: null,
      new_layout_component_selected_component: null,
      page_reference: null,

      is_delete_pageh_modal_open: false,
      page_to_be_deleted: null,

      //Notes
      edit_note_modal: false,
      label: null,
      description: null,
      background_color: null,
      note_id: null,
      current_rollup_metrics: [],
      share_page_is_open: false,
      shared_page: false,
      clone_page_is_open: false,
      cloned_page: false,

      change_organization_modal_open: false,

      //workspace
      new_workspace_modal_open: false,
      delete_workspaces_modal_open: false,
      confirm_delete_workspaces_checkbox: false,
      workspaces_to_delete: [],
      add_new_workspace_team_collaborator_modal_open: false,
      add_new_workspace_contact_collaborator_modal_open: false,
      workspace: null,
      workspace_user_to_be_deleted: null,
      delete_workspace_team_member_modal_open: false,
      delete_workspace_contact_modal_open: false,
      delete_workspace_page_modal_open: false,
      workspace_page_to_delete: null,
      change_workspaces_organization_modal_open: false,

      //Key records
      edit_key_records_modal_open: false,
      empty_key_records_modal_open: false,

      //Page settings
      page_settings_modal_open: false,
      current_item: null,
      disable_pages_grouping: false,
      setup_forecasting_modal_open: false,
      add_new_record_modal_open: false,
      add_new_template_modal_open: false,
      find_leads_locked_modal_open: false,
      share_activities_modal_is_open: false,
      invite_teammates_modal_is_open: false,
      object: null,

      //Map custom fields
      map_custom_fields_modal_open: false,
      map_custom_fields_record_to_map: null,
      map_custom_record_object_type: "company",
      map_custom_record_all_metrics: null,

      //Survey
      survey: null,
      survey_modal_open: false,
      select_survey_modal_open: false,
      select_survey_modal_open_after_submission: false,
      send_survey_modal_open: false,
      survey_responses_modal_open: false,

      is_feedback_form_modal_open: false
    }
  },
  computed: {
    all_tabs() {
      if (this.current_page_reference.includes("company_profile")) {
        return this.$store.state.portfolio?.object_types
      } else {
        return this.$store.state.portfolio?.object_types.filter((v) => v.type != this.$route.params.object_type)
      }
    },

    all_rollup_metrics() {
      if (this.current_object_type !== "default" && this.current_object_type) {
        return this.$store.state.metrics.filter((m) => m.aggregation_method && m.object_type == this.current_object_type) || []
      } else {
        return this.$store.state.metrics.filter((m) => m.aggregation_method) || []
      }
    },

    current_tabs() {
      return this.getPageTabs({ page_reference: this.current_page_reference })
    },

    all_table_metrics() {
      let ids = this.current_table_metrics.filter((x) => x).map((m) => m.id)
      let list = this.$store.state.metrics
        .filter((m) => m.object_type == this.current_object_type)
        .concat([
          {
            object_type: "record",
            id: "top_parent",
            reference_name: "top_parent",
            type: "field",
            name: this.$t("Top Parent"),
            data_type: "String"
          },
          {
            object_type: "record",
            id: "origins",
            reference_name: "origins",
            type: "field",
            name: this.$t("Origin"),
            data_type: "String"
          },
          {
            object_type: "record",
            id: "parent__id",
            reference_name: "parent__id",
            type: "field",
            name: this.$t("Parent"),
            data_type: "String"
          },
          {
            object_type: "record",
            id: "owner__id",
            reference_name: "owner__id",
            type: "field",
            name: this.$t("Owner"),
            data_type: "String"
          },
          {
            object_type: "record",
            id: "co_owners__id",
            reference_name: "co_owners__id",
            type: "field",
            name: this.$t("Co-Owners"),
            data_type: "List"
          },
          {
            object_type: "record",
            id: "tags__name",
            reference_name: "tags__name",
            type: "field",
            name: this.$t("Assigned Tags"),
            data_type: "List"
          }
        ])

      return this.$utils.sortObjectsList(list, ids, null).filter((x) => x)
    },
    current_table_metrics() {
      let metrics_list = this.getViewMetrics({
        page_reference: this.current_page_reference,
        object_type: this.current_object_type,
        view_type: this.current_view_type,
        layout_page_reference: this.layout_page_reference,
        component_reference: this.component_reference
      })
        .filter((x) => x)
        .map((ref) => this.$utils.getMetricByReferenceName(ref))

      return metrics_list
    },
    all_graphs() {
      return this.$store.state.data.all_graphs
    },
    current_graphs() {
      return this.getPageGraphs({
        page_reference: this.current_page_reference,
        object_type: this.current_object_type
      })
    },
    all_forecast_table_metrics() {
      return [
        {
          label: this.$t("Account"),
          id: "account"
        },
        {
          label: this.$t("Owner"),
          id: "owner"
        },
        {
          label: this.$t("Co-owners"),
          id: "co_owners"
        },
        // {
        //   label: this.$t()"Parent",
        //   id: "parent"
        // },
        {
          label: this.$t("Tags"),
          id: "tags"
        },
        {
          label: this.$t("Type"),
          id: "forecast_type"
        },
        {
          label: this.$t("Date"),
          id: "forecasted_date"
        },
        {
          label: this.$t("Base Value"),
          id: "base_value"
        },
        {
          label: this.$t("Original Base"),
          id: "original_base_value"
        },
        {
          label: this.$t("Status"),
          id: "forecast_status"
        },
        {
          label: this.$t("Forecasted Value"),
          id: "forecasted_value"
        },
        {
          label: this.$t("Best Case"),
          id: "forecasted_value_max"
        },
        {
          label: this.$t("Worst Case"),
          id: "forecasted_value_min"
        },
        {
          label: this.$t("GRR"),
          id: "grr"
        },
        {
          label: this.$t("NRR"),
          id: "nrr"
        },
        {
          label: this.$t("Delta%"),
          id: "delta_percentage"
        },
        {
          label: this.$t("Delta"),
          id: "delta"
        },
        {
          label: this.$t("Flag"),
          id: "flag"
        }
      ]
    },
    all_leads_table_metrics() {
      return [
        {
          label: this.$t("Origin"),
          id: "origin"
        },
        {
          label: this.$t("Type"),
          id: "type"
        },
        {
          label: this.$t("Name"),
          id: "name"
        },
        {
          label: this.$t("Owner"),
          id: "owner"
        },
        {
          label: this.$t("Added at"),
          id: "added_at"
        }
      ]
    },
    all_headers() {
      if (this.type == "Plans" || this.type == "Goals" || this.type == "Tasks") {
        return [
          {
            label: this.$t("Status"),
            name: this.$t("Status"),
            id: "status"
          },
          {
            label: this.$t("Name"),
            name: this.$t("Name"),
            id: "name"
          },
          {
            label: this.$t("Company/Opportunity"),
            name: this.$t("Company/Opportunity"),
            id: "companies"
          },
          {
            label: this.$t("Assigned Tags"),
            name: this.$t("Assigned Tags"),
            id: "tags"
          },
          {
            label: this.$t("Contacts"),
            name: this.$t("Contacts"),
            id: "contacts"
          },
          {
            label: this.$t("Team Members"),
            name: this.$t("Team Members"),
            id: "collaborators"
          },
          {
            label: this.$t("Start Date"),
            name: this.$t("Start Date"),
            id: "start_date"
          },
          {
            label: this.$t("Due Date"),
            name: this.$t("Due Date"),
            id: "date"
          }
        ]
      } else if (this.type == "Interactions") {
        return [
          {
            label: this.$t("Type"),
            name: this.$t("Type"),
            id: "type"
          },
          {
            label: this.$t("Category"),
            name: this.$t("Category"),
            id: "category"
          },
          {
            label: this.$t("Company/Opportunity"),
            name: this.$t("Company/Opportunity"),
            id: "companies"
          },
          {
            label: this.$t("Assigned Tags"),
            name: this.$t("Assigned Tags"),
            id: "tags"
          },
          {
            label: this.$t("Contacts"),
            name: this.$t("Contacts"),
            id: "contacts"
          },
          {
            label: this.$t("Team Members"),
            name: this.$t("Team Members"),
            id: "collaborators"
          },
          {
            label: this.$t("Date"),
            name: this.$t("Date"),
            id: "date"
          }
        ]
      } else {
        return []
      }
    }
  },

  created() {
    if (this.getViewMetrics) {
      this.current_rollup_metrics = this.getViewMetrics({
        page_reference: this.current_page_reference,
        layout_page_reference: this.layout_page_reference,
        component_reference: this.component_reference,
        object_type: this.current_object_type,
        view_type: this.current_view_type,
        current_forecast_name: this.current_forecast_name
      }).map((ref) => this.$utils.getMetricByReferenceName(ref))
    }
    this.register_listeners()
  },

  methods: {
    chooseFirstName(metric, record) {
      record["value"] = {
        first_name: metric.reference_name,
        last_name: record["value"]?.last_name ? record["value"]?.last_name : null,
        email: record["value"]?.email ? record["value"]?.email : null
      }
    },

    chooseLastName(metric, record) {
      record["value"] = {
        last_name: metric.reference_name,
        first_name: record["value"]?.first_name ? record["value"]?.first_name : null,
        email: record["value"]?.email ? record["value"]?.email : null
      }
    },

    chooseEmail(metric, record) {
      record["value"] = {
        email: metric.reference_name,
        first_name: record["value"]?.first_name ? record["value"]?.first_name : null,
        last_name: record["value"]?.last_name ? record["value"]?.last_name : null
      }
    },
    confirmEmptyKeyRecords() {
      this.$store
        .dispatch("data/updateRecord", {
          record_id: this.record.id,
          data: { key_records: [] }
        })
        .then((res) => {
          this.$emitter.emit(ALL_EMITTERS.UPDATE_KEY_RECORDS_TIED_RECORD)
          this.$store.dispatch("ui/setCurrentCompany", res.data)
        })
    },
    updateWorkspacesList(showIsDone, closeModal) {
      this.$store.dispatch(`data/update${this.to_be_shared_object.type}`, {
        [this.to_be_shared_object.type.toLowerCase()]: {
          id: this.to_be_shared_object.id,
          workspaces_shared_in: this.workspaces_selected.map((w) => w.id)
        }
      })
      showIsDone()
      closeModal()
    },
    recordIdToSendToWorkspaceSelection(object) {
      if (object.companies) {
        if (object.companies.length) {
          return object.companies[0].id
        }
        return null
      }
      return object.record?.id
    },
    removeWorkspacePage(showIsDone) {
      this.$store
        .dispatch("data/updateWorkspace", {
          short_id: this.workspace?.short_id,
          layout: this.workspace.layout.filter((l) => l.id !== this.workspace_page_to_delete.id)
        })
        .then(() => {
          this.$emitter.emit(ALL_EMITTERS.WORKSPACE_UPDATED_IN_PAGES)
          showIsDone()
        })
    },
    removeWorkspaceTeamMember(showIsDone) {
      this.$store.dispatch("data/removeWorkspaceTeamMember", {
        short_id: this.workspaces_to_delete,
        user: this.workspace_user_to_be_deleted
      })
      showIsDone()
    },

    removeWorkspaceContact(showIsDone) {
      this.$store.dispatch("data/removeWorkspaceCollaborator", {
        short_id: this.workspaces_to_delete,
        contact: this.workspace_user_to_be_deleted
      })
      showIsDone()
    },

    deleteSelectedWorkspace(showIsDone) {
      this.$emitter.emit(ALL_EMITTERS.CONFIRM_DELETE_WORKSPACES, this.workspaces_to_delete)
      showIsDone()
    },
    getCurrentLeadsTableMetrics() {
      if (this.$store.state.ui_options.leads_table_headers && this.$store.state.ui_options.leads_table_headers[this.component_reference]) {
        return this.$store.state.ui_options.leads_table_headers[this.component_reference].map((x) => {
          return {
            ...this.all_leads_table_metrics.find((a) => a.id == x),
            id: x
          }
        })
      }
      return []
    },
    getCurrentActivitiesTableHeaders() {
      if (this.type == "Plans") {
        if (
          this.$store.state.ui_options.plans_activities_table_headers &&
          this.$store.state.ui_options.plans_activities_table_headers[this.component_reference]
        ) {
          return this.$store.state.ui_options.plans_activities_table_headers[this.component_reference].map((x) => {
            return {
              ...this.all_headers.find((a) => a.id == x),
              id: x
            }
          })
        }
      } else if (this.type == "Goals") {
        if (
          this.$store.state.ui_options.goals_activities_table_headers &&
          this.$store.state.ui_options.goals_activities_table_headers[this.component_reference]
        ) {
          return this.$store.state.ui_options.goals_activities_table_headers[this.component_reference].map((x) => {
            return {
              ...this.all_headers.find((a) => a.id == x),
              id: x
            }
          })
        }
      } else if (this.type == "Tasks") {
        if (
          this.$store.state.ui_options.tasks_activities_table_headers &&
          this.$store.state.ui_options.tasks_activities_table_headers[this.component_reference]
        ) {
          return this.$store.state.ui_options.tasks_activities_table_headers[this.component_reference].map((x) => {
            return {
              ...this.all_headers.find((a) => a.id == x),
              id: x
            }
          })
        }
      } else if (this.type == "Interactions") {
        if (
          this.$store.state.ui_options.interactions_activities_table_headers &&
          this.$store.state.ui_options.interactions_activities_table_headers[this.component_reference]
        ) {
          return this.$store.state.ui_options.interactions_activities_table_headers[this.component_reference].map((x) => {
            return {
              ...this.all_headers.find((a) => a.id == x),
              id: x
            }
          })
        }
      }
      return this.all_headers
    },
    getForecastTableMetrics() {
      if (
        this.$store.state.ui_options.forecast_table_headers &&
        this.$store.state.ui_options.forecast_table_headers[this.component_reference]
      ) {
        return this.$store.state.ui_options.forecast_table_headers[this.component_reference].map((x) => {
          return {
            ...this.all_forecast_table_metrics.find((a) => a.id == x),
            id: x
          }
        })
      }
      return []
    },
    removePhase(showIsDone) {
      dataService.removePhase(this.phase.id).then(() => {
        let index = this.selected_journey.phases.findIndex((x) => x.id == this.phase.id)
        this.selected_journey.phases.splice(index, 1)

        dataService
          .updateJourney(this.selected_journey.id, {
            phases: this.selected_journey.phases.map((p) => p.id)
          })
          .then(() => {
            showIsDone()
          })
      })
    },

    handleAddGoalTasksToPlan(closeModal, showIsDone) {
      this.$emitter.emit(ALL_EMITTERS.CONFIRM_ADD_GOAL_TASKS_TO_PLAN_CREATE_FORM, this.goals_with_tasks)
      this.$emitter.emit(ALL_EMITTERS.CONFIRM_ADD_GOAL_TASKS_TO_PLAN_UPDATE_FORM, this.goals_with_tasks)
      closeModal()
      showIsDone()
    },

    deleteSelectedInteraction(showIsDone) {
      this.$store
        .dispatch("data/removeInteraction", {
          interaction: this.current_interaction
        })
        .then(() => {
          this.delete_interaction_modal_is_open = false
          this.current_interaction = null
          showIsDone()
        })
    },

    deleteSelectedTask(showIsDone) {
      this.$store.dispatch("data/removeTask", { task: this.current_task }).then(() => {
        this.delete_task_modal_is_open = false
        this.current_task = null
        showIsDone()
      })
    },

    deleteSelectedGoal(showIsDone) {
      this.$store.dispatch("data/removeGoal", { goal: this.current_goal }).then(() => {
        this.delete_goal_modal_is_open = false
        this.current_goal = null
        showIsDone()
      })
    },

    deleteSelectedPlan(showIsDone) {
      this.$store.dispatch("data/removePlan", { plan: this.current_plan }).then(() => {
        this.delete_plan_modal_is_open = false
        this.current_plan = null
        showIsDone()
      })
    },
    //delete
    deleteSelectedGroup(callback) {
      dataService.deleteGroup(this.group.id).then(() => {
        callback()
        this.$store.dispatch("data/getGroups")
        this.$emitter.emit(ALL_EMITTERS.SUCCESSFULLY_DELETED, this.group.id)
      })
    },
    deleteSelectedTemplate(callback) {
      dataService.removeTemplate(this.template.id).then(() => {
        this.$emitter.emit(ALL_EMITTERS.TEMPLATE_DELETED, this.template.id)
        callback()
      })
    },

    resetCurrentValues() {
      // this.current_filter = null
      // this.current_graph = null
    },

    register_listeners() {
      this.$emitter.on(ALL_EMITTERS.MAP_CUSTOM_FIELDS, ({ record, object_type, all_metrics }) => {
        this.map_custom_fields_modal_open = true
        this.map_custom_fields_record_to_map = record
        this.map_custom_record_object_type = object_type ? object_type : "company"
        this.map_custom_record_all_metrics = all_metrics
      })

      this.$emitter.on(ALL_EMITTERS.OPEN_PAGE_SETTINGS, (current_item) => {
        this.disable_pages_grouping =
          current_item.name == "company_profile" || current_item.name === "object_profile" || current_item.name === "contact_profile"
        this.current_item = current_item
        this.page_settings_modal_open = true
      })

      this.$emitter.on(ALL_EMITTERS.ADD_NEW_RECORD_MODAL, () => {
        this.add_new_record_modal_open = true
      })

      this.$emitter.on(ALL_EMITTERS.ADD_NEW_TEMPLATE_MODAL, () => {
        this.add_new_template_modal_open = true
      })

      this.$emitter.on(ALL_EMITTERS.SETUP_FORECASTING_MODAL, () => {
        this.setup_forecasting_modal_open = true
      })

      this.$emitter.on(ALL_EMITTERS.FIND_LEADS_LOCKED_MODAL, () => {
        this.find_leads_locked_modal_open = true
      })

      this.$emitter.on(ALL_EMITTERS.SHARE_ACTIVITY, (object) => {
        this.share_activities_modal_is_open = true
        this.object = object
      })
      this.$emitter.on(ALL_EMITTERS.INVITE_TEAMMATES, () => {
        this.invite_teammates_modal_is_open = true
      })

      this.$emitter.on(ALL_EMITTERS.CREATE_A_SURVEY, ({ records, survey = null }) => {
        if (this.select_survey_modal_open) {
          this.select_survey_modal_open = false
          this.select_survey_modal_open_after_submission = true
        }
        this.survey_modal_open = true
        if (survey) {
          this.survey = survey
        } else {
          this.survey = null
        }
        this.records = records
      })

      this.$emitter.on(ALL_EMITTERS.GENERIC_DELETE_MODAL, (generic_delete_item) => {
        this.generic_delete_item = generic_delete_item
        this.generic_delete_modal_open = true
      })
      this.$emitter.on(ALL_EMITTERS.SELECT_A_SURVEY, (records) => {
        this.select_survey_modal_open = true
        this.records = records
      })

      this.$emitter.on(ALL_EMITTERS.SEND_A_SURVEY, (survey) => {
        this.send_survey_modal_open = true
        this.survey = survey
      })

      this.$emitter.on(ALL_EMITTERS.SURVEY_RESPONSES, (survey) => {
        this.survey_responses_modal_open = true
        this.survey = survey
      })

      this.$emitter.on(ALL_EMITTERS.PROFILE_UPDATED_IN_MODALS, () => {
        this.current_rollup_metrics = this.getViewMetrics({
          page_reference: this.current_page_reference,
          layout_page_reference: this.layout_page_reference,
          component_reference: this.component_reference,
          object_type: this.current_object_type,
          view_type: this.current_view_type,
          current_forecast_name: this.current_forecast_name
        }).map((ref) => this.$utils.getMetricByReferenceName(ref))
      })

      this.$emitter.on(ALL_EMITTERS.EDIT_KEY_RECORDS, ({ record }) => {
        this.edit_key_records_modal_open = true
        this.record = record
      })

      this.$emitter.on(ALL_EMITTERS.EMPTY_KEY_RECORDS, ({ record }) => {
        this.empty_key_records_modal_open = true
        this.record = record
      })

      this.$emitter.on(ALL_EMITTERS.CHANGE_WORKSPACE_ORGANIZATION, () => {
        this.change_workspaces_organization_modal_open = true
      })

      this.$emitter.on(ALL_EMITTERS.CHANGE_ORGANIZATION, () => {
        this.change_organization_modal_open = true
      })

      this.$emitter.on(ALL_EMITTERS.REMOVE_WORKSPACE_PAGE, ({ page, workspace }) => {
        this.delete_workspace_page_modal_open = true
        this.workspace = workspace
        this.workspace_page_to_delete = page
      })

      this.$emitter.on(ALL_EMITTERS.REMOVE_WORKSPACE_TEAM_MEMBER, ({ short_id, user }) => {
        this.delete_workspace_team_member_modal_open = true
        this.workspaces_to_delete = short_id
        this.workspace_user_to_be_deleted = user
      })

      this.$emitter.on(ALL_EMITTERS.REMOVE_WORKSPACE_CONTACT, ({ short_id, user }) => {
        this.delete_workspace_contact_modal_open = true
        this.workspaces_to_delete = short_id
        this.workspace_user_to_be_deleted = user
      })

      this.$emitter.on(ALL_EMITTERS.DELETE_WORKSPACES, (workspace) => {
        this.delete_workspaces_modal_open = true
        this.workspaces_to_delete = workspace
        this.confirm_delete_workspaces_checkbox = false
      })
      this.$emitter.on(ALL_EMITTERS.CREATE_NEW_WORKSPACE, () => {
        this.new_workspace_modal_open = true
      }),
        this.$emitter.on(ALL_EMITTERS.ADD_NEW_WORKSPACE_TEAM_COLLABORATOR, (workspace) => {
          this.add_new_workspace_team_collaborator_modal_open = true
          this.workspace = workspace
        }),
        this.$emitter.on(ALL_EMITTERS.ADD_NEW_WORKSPACE_CONTACT_COLLABORATOR, (workspace) => {
          this.add_new_workspace_contact_collaborator_modal_open = true
          this.workspace = workspace
        }),
        this.$emitter.on(ALL_EMITTERS.ADD_GOAL_TASKS_TO_PLAN, ({ tasks_count, multiple_goals, goals_with_tasks }) => {
          this.tasks_count = tasks_count
          this.multiple_goals = multiple_goals
          this.goals_with_tasks = goals_with_tasks
          this.add_goal_tasks_to_plan_modal = true
        })
      this.$emitter.on(ALL_EMITTERS.DELETE_PAGE, (page) => {
        this.is_delete_pageh_modal_open = true
        this.page_to_be_deleted = page
      }),
        this.$emitter.on(ALL_EMITTERS.INVITE_TEAM_MEMBERS, () => {
          this.is_invite_team_members_modal_open = true
        }),
        this.$emitter.on(ALL_EMITTERS.DELETE_GRAPH, (graph) => {
          this.is_delete_graph_modal_open = true
          this.current_graph = graph
        }),
        this.$emitter.on(ALL_EMITTERS.EDIT_GRAPH, ({ graph: graph, page_reference: page_reference }) => {
          this.current_page_reference = page_reference
          this.current_graph = graph
          this.is_update_graph_modal_open = true
        }),
        this.$emitter.on(ALL_EMITTERS.CREATE_GRAPH, (data) => {
          this.current_filter = data?.filter
          this.current_page_reference = data?.page_reference
          this.current_object_type = data?.object_type
          this.is_create_graph_modal_open = true
        })

      this.$emitter.on(ALL_EMITTERS.ADD_NEW_COMPONENT, ({ page_reference, current_template, step = null, selected_component = null }) => {
        this.page_reference = page_reference
        this.current_template = current_template
        this.add_new_component_modal = true
        this.new_layout_component_initial_step = step
        this.new_layout_component_selected_component = selected_component
      }),
        this.$emitter.on(ALL_EMITTERS.CURRENT_TEMPLATE_UPDATED, ({ current_template }) => {
          this.current_template = current_template
        }),
        this.$emitter.on(ALL_EMITTERS.SCORE_CREATED_FOR_MODAL, ({ score, page_reference }) => {
          this.add_new_component_modal = true
          this.page_reference = page_reference
          setTimeout(() => {
            this.$emitter.emit(ALL_EMITTERS.ADD_SCORE_TO_COMPONENTS_LIST, score)
          }, 200)
        }),
        this.$emitter.on(ALL_EMITTERS.METRIC_CREATED_FOR_MODAL, ({ metric, page_reference }) => {
          this.add_new_component_modal = true
          this.page_reference = page_reference
          setTimeout(() => {
            this.$emitter.emit(ALL_EMITTERS.ADD_METRIC_TO_COMPONENTS_LIST, metric)
          }, 200)
        }),
        this.$emitter.on(ALL_EMITTERS.CLOSE_ADD_NEW_COMPONENT, () => {
          this.add_new_component_modal = false
        }),
        this.$emitter.on(ALL_EMITTERS.CREATE_CUSTOM_METRIC, ({ object_type: object_type, view_type: view_type }) => {
          this.current_object_type = object_type
          this.current_view_type = view_type
          this.is_created_custom_metric_modal_open = true
        })
      this.$emitter.on(ALL_EMITTERS.FEEDBACK_FORM, () => {
        this.is_feedback_form_modal_open = true
      })

      this.$emitter.on(ALL_EMITTERS.UPDATE_TABS, (page_reference) => {
        this.first_portfolio_tab = this.getPageTabs({
          page_reference: "portfolio"
        })[0]
        this.current_page_reference = page_reference
        this.is_update_tabs_modal_open = true
      })
      this.$emitter.on(ALL_EMITTERS.UPDATE_FORECAST_TABLE_METRICS, (component_reference) => {
        this.is_update_forecast_table_modal_open = true
        this.component_reference = component_reference
      })
      this.$emitter.on(ALL_EMITTERS.UPDATE_LEADS_TABLE_METRICS, (component_reference) => {
        this.is_update_leads_table_modal_open = true
        this.component_reference = component_reference
      })
      this.$emitter.on(ALL_EMITTERS.UPDATE_ACTIVITIES_TABLE_HEADERS, ({ component_reference: component_reference, type: type }) => {
        this.type = type
        this.component_reference = component_reference
        this.is_update_activities_table_modal_open = true
      })
      this.$emitter.on(ALL_EMITTERS.ADD_NOTES_FORECAST, ({ forecast_id: forecast_id, company: company }) => {
        this.forecast_id = forecast_id
        this.record = company
        this.is_add_notes_forecasting_modal_open = true
      })
      this.$emitter.on(ALL_EMITTERS.ADD_NOTES_RECORD, ({ company: company }) => {
        this.record = company
        this.is_add_notes_record_modal_open = true
      })
      this.$emitter.on(
        ALL_EMITTERS.UPDATE_METRICS,
        ({
          page_reference: page_reference,
          object_type: object_type,
          view_type: view_type,
          layout_page_reference,
          component_reference
        }) => {
          this.current_object_type = object_type
          this.current_page_reference = page_reference
          this.current_view_type = view_type
          this.is_update_table_metrics_modal_open = true
          this.layout_page_reference = layout_page_reference
          this.component_reference = component_reference
        }
      )

      this.$emitter.on(
        ALL_EMITTERS.UPDATE_ROLLUP_METRICS,
        ({
          page_reference: page_reference,
          object_type: object_type,
          view_type: view_type,
          current_forecast_name: current_forecast_name,
          layout_page_reference,
          component_reference
        }) => {
          this.current_page_reference = page_reference
          this.current_object_type = object_type
          this.current_view_type = view_type
          this.current_forecast_name = current_forecast_name
          this.is_rollup_metrics_modal_open = true
          this.layout_page_reference = layout_page_reference
          this.component_reference = component_reference
        }
      )

      this.$emitter.on(ALL_EMITTERS.SHOW_HIDE_GRAPHS, ({ page_reference: page_reference, object_type: object_type }) => {
        this.current_page_reference = page_reference
        this.current_object_type = object_type
        this.is_graphs_modal_open = true
      })

      this.$emitter.on(
        ALL_EMITTERS.GENERATE_EXTERNAL_SCORE,
        ({ full_records: full_records, object_type = null, excluded_records = [], is_all_records = false }) => {
          this.full_records = full_records
          this.is_external_score_modal_open = true
          this.current_object_type = object_type
          this.excluded_records = excluded_records
          this.is_all_records = is_all_records
        }
      )

      this.$emitter.on(ALL_EMITTERS.CREATE_INTERACTION, (data) => {
        this.new_interaction_modal_is_open = true

        if (data?.is_all_records) {
          this.is_all_records = true
        } else {
          this.is_all_records = false
        }

        if (data?.excluded_records?.length) {
          this.excluded_records = data.excluded_records
        } else {
          this.excluded_records = []
        }

        if (data?.object_type) {
          this.current_object_type = data.object_type
        } else {
          this.current_object_type = null
        }

        if (data?.count) {
          this.total_records_count = data.count
        } else {
          this.total_records_count = null
        }

        if (data?.records) {
          this.records = data.records
        } else {
          this.records = []
        }

        if (data?.contact) {
          this.selected_contact = data.contact
        } else {
          this.selected_contact = null
        }
        if (data?.record) {
          this.record = data.record
        } else {
          this.record = null
        }
        if (data?.selected_template) {
          this.selected_template = data.selected_template
        } else {
          this.selected_template = null
        }
        if (data?.is_edit_template) {
          this.is_edit_template = true
        } else {
          this.is_edit_template = false
        }
      })

      this.$emitter.on(ALL_EMITTERS.UPDATE_INTERACTION, ({ interaction, is_template }) => {
        if (interaction?.id) {
          dataService.getInteraction(interaction.id).then((res) => {
            this.current_interaction = res.data
            this.is_template = is_template
            this.update_interaction_modal_is_open = true
          })
        } else {
          this.current_interaction = interaction
          this.is_template = is_template
          this.update_interaction_modal_is_open = true
        }
      })

      this.$emitter.on(ALL_EMITTERS.DELETE_INTERACTION, ({ interaction }) => {
        this.current_interaction = interaction
        this.delete_interaction_modal_is_open = true
      })

      this.$emitter.on(ALL_EMITTERS.UPDATE_TASK, ({ task, is_template, in_playbook }) => {
        if (task?.id && !task.for_template) {
          dataService.getTask(task.id).then((res) => {
            this.current_task = res.data
            this.goals = res.data.goals
            this.is_template = is_template
            this.in_playbook = in_playbook
            this.update_task_modal_is_open = true
          })
        } else {
          this.current_task = task
          this.goals = []
          this.is_template = is_template
          this.in_playbook = in_playbook
          this.update_task_modal_is_open = true
        }
      })

      this.$emitter.on(ALL_EMITTERS.DELETE_TASK, ({ task }) => {
        this.current_task = task
        this.delete_task_modal_is_open = true
      })

      this.$emitter.on(ALL_EMITTERS.UPDATE_GOAL, ({ goal, is_template }) => {
        if (goal?.id && !goal.for_template) {
          dataService.getGoal(goal.id).then((res) => {
            this.current_goal = res.data
            this.is_template = is_template
            this.update_goal_modal_is_open = true
          })
        } else {
          this.current_goal = goal
          this.is_template = is_template
          this.update_goal_modal_is_open = true
        }
      })

      this.$emitter.on(ALL_EMITTERS.DELETE_GOAL, ({ goal }) => {
        this.current_goal = goal
        this.delete_goal_modal_is_open = true
      })

      this.$emitter.on(ALL_EMITTERS.UPDATE_PLAN, ({ plan, values, from_expand, is_template }) => {
        this.plan_modal_width = "max-w-[900px] mx-5"
        if (plan?.id) {
          dataService.getPlan(plan.id).then((res) => {
            this.current_plan = res.data
            this.is_template = is_template
            this.update_plan_modal_is_open = true
            this.plan_values = values
            this.from_expand = from_expand
          })
        } else {
          this.current_plan = plan
          this.is_template = is_template
          this.update_plan_modal_is_open = true
          this.plan_values = values
          this.from_expand = from_expand
        }
      })

      this.$emitter.on(ALL_EMITTERS.DELETE_PLAN, ({ plan }) => {
        this.current_plan = plan
        this.delete_plan_modal_is_open = true
      })

      this.$emitter.on(ALL_EMITTERS.CREATE_GROUP, ({ filter, object_type }) => {
        this.current_filter = filter
        this.current_object_type = object_type
        this.new_group_modal_is_open = true
      })

      this.$emitter.on(ALL_EMITTERS.DELETE_GROUP, ({ group }) => {
        this.group = group
        this.show_delete_group_confirmation_modal = true
      })
      this.$emitter.on(ALL_EMITTERS.DELETE_TEMPLATE, ({ template }) => {
        this.template = template
        this.show_delete_template_confirmation_modal = true
      })
      this.$emitter.on(ALL_EMITTERS.ADD_OBJECT_RECORD, (object_type) => {
        this.new_record_object_type = object_type
        this.show_add_new_record = true
      })

      this.$emitter.on(ALL_EMITTERS.EDIT_GROUP, ({ group, filter }) => {
        this.group = group
        this.current_filter = filter
        this.show_edit_group_confirmation_modal = true
      })

      this.$emitter.on(ALL_EMITTERS.CREATE_GOAL, (data) => {
        if (data?.is_all_records) {
          this.is_all_records = true
        } else {
          this.is_all_records = false
        }

        if (data?.excluded_records?.length) {
          this.excluded_records = data.excluded_records
        } else {
          this.excluded_records = []
        }

        if (data?.object_type) {
          this.current_object_type = data.object_type
        } else {
          this.current_object_type = null
        }

        if (data?.count) {
          this.total_records_count = data.count
        } else {
          this.total_records_count = null
        }
        if (data?.inMilestone) {
          this.in_milestone = true
        }
        if (data?.inPlan) {
          this.in_plan = true
        }
        if (data?.contact) {
          this.selected_contact = data.contact
        } else {
          this.selected_contact = null
        }
        if (data?.records) {
          this.records = data.records
        } else {
          this.records = []
        }
        if (data?.record) {
          this.record = data.record
        } else {
          this.record = null
        }
        if (data?.selected_template) {
          this.selected_template = data.selected_template
        } else {
          this.selected_template = null
        }
        if (data?.is_edit_template) {
          this.is_edit_template = true
        } else {
          this.is_edit_template = false
        }
        if (data?.closeCallBack) {
          this.closeCallBack = data.closeCallBack
        } else {
          this.closeCallBack = () => {}
        }
        this.new_goal_modal_is_open = true
      })

      this.$emitter.on(ALL_EMITTERS.CREATE_TASK, (data) => {
        if (data?.is_all_records) {
          this.is_all_records = true
        } else {
          this.is_all_records = false
        }

        if (data?.excluded_records?.length) {
          this.excluded_records = data.excluded_records
        } else {
          this.excluded_records = []
        }

        if (data?.object_type) {
          this.current_object_type = data.object_type
        } else {
          this.current_object_type = null
        }

        if (data?.count) {
          this.total_records_count = data.count
        } else {
          this.total_records_count = null
        }

        if (data?.inPlan) {
          this.in_plan = true
        }
        if (data?.contact) {
          this.selected_contact = data.contact
        } else {
          this.selected_contact = null
        }
        if (data?.inGoal) {
          this.in_goal = true
        }
        if (data?.inInteraction) {
          this.in_interaction = true
        }
        if (data?.inMilestone) {
          this.in_milestone = true
        }
        if (data?.records) {
          this.records = data.records
        } else {
          this.records = []
        }
        if (data?.record) {
          this.record = data.record
        } else {
          this.record = null
        }
        if (data?.selected_template) {
          this.selected_template = data.selected_template
        } else {
          this.selected_template = null
        }
        if (data?.is_edit_template) {
          this.is_edit_template = true
        } else {
          this.is_edit_template = false
        }
        if (data?.closeCallBack) {
          this.closeCallBack = data.closeCallBack
        } else {
          this.closeCallBack = () => {}
        }
        this.new_task_defaults = data
        if (!this.new_task_modal_is_open) {
          this.new_task_modal_is_open = true
        }
      })

      this.$emitter.on(ALL_EMITTERS.CREATE_PLAN, (data) => {
        this.plan_modal_width = "max-w-[900px] mx-5"

        if (data?.is_all_records) {
          this.is_all_records = true
        } else {
          this.is_all_records = false
        }

        if (data?.excluded_records?.length) {
          this.excluded_records = data.excluded_records
        } else {
          this.excluded_records = []
        }

        if (data?.object_type) {
          this.current_object_type = data.object_type
        } else {
          this.current_object_type = null
        }

        if (data?.count) {
          this.total_records_count = data.count
        } else {
          this.total_records_count = null
        }

        if (data?.records) {
          this.records = data.records
        } else {
          this.records = []
        }

        if (data?.contact) {
          this.selected_contact = data.contact
        } else {
          this.selected_contact = null
        }
        if (data?.record) {
          this.record = data.record
        } else {
          this.record = null
        }
        if (data?.values) {
          this.plan_values = data.values
        }
        if (data?.from_expand) {
          this.from_expand = data?.from_expand
        } else {
          this.from_expand = false
        }
        if (data?.selected_template) {
          this.selected_template = data.selected_template
        } else {
          this.selected_template = null
        }
        if (data?.is_edit_template) {
          this.is_edit_template = true
        } else {
          this.is_edit_template = false
        }

        this.new_plan_modal_is_open = true
      })

      this.$emitter.on(ALL_EMITTERS.CREATE_OWNER, (data) => {
        if (data?.filter) {
          this.current_filter = data.filter
        }

        if (data?.is_all_records) {
          this.is_all_records = true
        } else {
          this.is_all_records = false
        }

        if (data?.excluded_records?.length) {
          this.excluded_records = this.$utils.clone(data.excluded_records)
        } else {
          this.excluded_records = []
        }

        if (data?.object_type) {
          this.current_object_type = data.object_type
        } else {
          this.current_object_type = null
        }

        if (data?.count) {
          this.total_records_count = data.count
        } else {
          this.total_records_count = null
        }

        if (data?.full_records?.length) {
          this.full_records = data.full_records
        } else {
          this.full_records = null
        }
        if (data?.filter) {
          this.current_filter = data?.filter
        } else {
          this.current_filter = null
        }
        if (data?.records?.length) {
          this.records = data.records
          this.record = null
        } else {
          if (data?.record) {
            this.record = data.record
            this.records = []
          } else {
            this.record = null
          }
        }
        this.new_owner_modal_is_open = true
      })

      this.$emitter.on(ALL_EMITTERS.CREATE_NEW_GROUP, (object_type) => {
        this.create_group_modal_is_open = true
        this.current_object_type = object_type
      })

      this.$emitter.on(ALL_EMITTERS.ASSIGN_TAGS, (data) => {
        if (data?.count) {
          this.total_records_count = data?.count
        } else {
          this.total_records_count = null
        }

        if (data?.is_all_records) {
          this.is_all_records = true
        } else {
          this.is_all_records = false
        }
        if (data?.excluded_records) {
          this.excluded_records = data.excluded_records
        } else {
          this.excluded_records = []
        }
        if (data?.full_records?.length) {
          this.full_records = data.full_records
        } else {
          this.full_records = null
        }

        this.current_object_type = data?.object_type
        if (data?.records?.length) {
          this.records = data.records
          this.record = null
        } else {
          if (data?.record) {
            this.record = data.record
            this.records = null
          } else {
            this.record = null
          }
        }
        if (data?.filter) {
          this.current_filter = data.filter
        } else {
          this.current_filter = null
        }
        this.assign_tags_modal_is_open = true
      })

      this.$emitter.on(ALL_EMITTERS.CREATE_NEW_PLAYBOOK, ({ playbook = null, playbooks = [] }) => {
        if (playbook) {
          this.playbook = playbook
        } else {
          this.playbook = null
        }

        if (playbooks.length) {
          this.playbooks = playbooks
        }
        this.create_playbook_modal_is_open = true
      })

      this.$emitter.on(ALL_EMITTERS.CHANGE_CO_OWNERS, (data) => {
        if (data?.filter) {
          this.current_filter = data.filter
        }

        if (data?.is_all_records) {
          this.is_all_records = true
        } else {
          this.is_all_records = false
        }

        if (data?.excluded_records?.length) {
          this.excluded_records = this.$utils.clone(data.excluded_records)
        } else {
          this.excluded_records = []
        }

        if (data?.object_type) {
          this.current_object_type = data.object_type
        } else {
          this.current_object_type = null
        }

        if (data?.count) {
          this.total_records_count = data?.count
        } else {
          this.total_records_count = null
        }
        if (data?.filter) {
          this.current_filter = data?.filter
        } else {
          this.current_filter = null
        }
        if (data?.full_records) {
          this.full_records = data.full_records
        } else {
          this.full_records = null
        }
        if (data?.records) {
          this.records = data.records
          this.record = null
        } else {
          if (data?.record) {
            this.record = data.record
            this.records = null
          } else {
            this.record = null
          }
        }
        this.co_owner_modal_is_open = true
      })

      this.$emitter.on(ALL_EMITTERS.ADD_TO_EXPANSION_LIST, (data) => {
        if (data?.is_all_records) {
          this.is_all_records = true
        } else {
          this.is_all_records = false
        }

        if (data?.excluded_records?.length) {
          this.excluded_records = this.$utils.clone(data.excluded_records)
        } else {
          this.excluded_records = []
        }

        if (data?.object_type) {
          this.current_object_type = data.object_type
        } else {
          this.current_object_type = null
        }

        if (data?.records) {
          this.records = data.records
        } else {
          this.records = []
        }
        if (data?.record) {
          this.record = data.record
        } else {
          this.record = null
        }
        if (data?.filter) {
          this.current_filter = data?.filter
        } else {
          this.current_filter = null
        }
        this.add_to_expansion_list_modal_is_open = true
      })

      this.$emitter.on(ALL_EMITTERS.SELECTED_EXPANSION_LIST, (list) => {
        this.expansion_list = list
      })

      this.$emitter.on(ALL_EMITTERS.REMOVE_FROM_EXPANSION_LIST, (data) => {
        if (data?.records) {
          this.records = data.records
        } else {
          this.records = []
        }
        this.is_remove_from_expansion_list_modal_open = true
      })

      this.$emitter.on(ALL_EMITTERS.DELETE_EXPANSION_LIST, (list) => {
        this.is_delete_expansion_list_modal_open = true
        this.expansion_list = list
      })

      this.$emitter.on(ALL_EMITTERS.ADD_COMPANY, () => {
        this.add_company_modal_is_open = true
      }),
        this.$emitter.on(ALL_EMITTERS.PROFILE_SUCCESS_CASE, () => {
          this.is_form_updated = true
        })
      this.$emitter.on(ALL_EMITTERS.REMOVE_SEARCH_ITEM, ({ search_item }) => {
        this.prospecting_search_item_deleted = search_item
        this.delete_prospecting_saved_search = true
      })

      this.$emitter.on(ALL_EMITTERS.COLORS_CHANGED, () => {
        this.colors_ranges_save = true
      })

      this.$emitter.on(ALL_EMITTERS.EDIT_METRIC, ({ metric }) => {
        this.metric_settings_modal_open = true
        this.selected_metric = metric
      })

      this.$emitter.on(ALL_EMITTERS.DELETE_PHASE, ({ phase, selected_journey }) => {
        this.phase = phase
        this.selected_journey = selected_journey
        this.is_delete_phase_modal_open = true
      })

      this.$emitter.on(ALL_EMITTERS.EDIT_CUSTOM_METRIC, ({ metric }) => {
        this.custom_metric_edit_modal_open = true
        this.selected_metric = metric
      })

      this.$emitter.on(ALL_EMITTERS.EDIT_COMPUTED_METRIC, ({ metric }) => {
        this.computed_metric_edit_modal_open = true
        this.selected_metric = metric
      })

      this.$emitter.on(ALL_EMITTERS.EDIT_LIST_METRIC, ({ metric }) => {
        this.list_metric_modal_open = true
        this.list_metric = metric
      })

      this.$emitter.on(ALL_EMITTERS.EDIT_NOTE, ({ label, description, background_color, id }) => {
        this.label = label
        this.description = description
        this.background_color = background_color
        this.note_id = id
        this.edit_note_modal = true
      })

      this.$emitter.on(
        ALL_EMITTERS.BULK_RECORDS_METRIC_UPDATE,
        ({ records, object_type, excluded_records = [], is_all_records = false, count = null, is_leads_records = false }) => {
          this.is_leads_records = is_leads_records
          this.records = records
          this.total_records_count = count
          this.excluded_records = excluded_records
          this.is_all_records = is_all_records
          this.current_object_type = object_type
          this.show_bulk_records_metric_update_model = true
        }
      )
      this.$emitter.on(
        ALL_EMITTERS.MERGE_RECORDS_METRICS,
        ({ records, object_type, excluded_records = [], is_all_records = false, count = null, is_leads_records = false }) => {
          this.is_leads_records = is_leads_records
          this.records = records
          this.total_records_count = count
          this.excluded_records = excluded_records
          this.is_all_records = is_all_records
          this.current_object_type = object_type
          this.merge_records_metrics_model = true
        }
      )
      this.$emitter.on(
        ALL_EMITTERS.DELETE_SELECTED_RECORDS,
        ({ filter = "", records, object_type, excluded_records = [], is_all_records = false, count = null, is_leads_records = false }) => {
          this.is_leads_records = is_leads_records
          this.records = records
          this.total_records_count = count
          this.excluded_records = excluded_records
          this.is_all_records = is_all_records
          this.current_object_type = object_type
          this.show_delete_records_confirmation_modal = true
          this.current_filter = filter
        }
      )
      this.$emitter.on(ALL_EMITTERS.MODULE_IS_NOT_ACTIVATED, (module) => {
        this.module_not_activated_modal = true
        this.not_activated_module = module
      }),
        this.$emitter.on(ALL_EMITTERS.ADD_NEW_JOURNEY, () => {
          this.add_new_journey_modal_is_open = true
        }),
        this.$emitter.on(ALL_EMITTERS.EDIT_JOURNEY, ({ journey }) => {
          this.edit_journey_modal_is_open = true
          this.selected_journey = journey
        }),
        this.$emitter.on(ALL_EMITTERS.DELETE_JOURNEY, ({ journey }) => {
          this.delete_journey = true
          this.journey_deleted = journey
        }),
        this.$emitter.on(ALL_EMITTERS.DELETE_COLOR, (color) => {
          this.delete_color = true
          this.color_deleted = color
        }),
        this.$emitter.on(
          ALL_EMITTERS.GRAPH_DETAIL_MODAL,
          ({ graph_label_value, graph_metric, filter, truncate_by, graph_rollup_metric = null }) => {
            if (graph_metric?.object_type !== "contact") {
              this.graph_detail_modal_open = true
              this.graph_label_value = graph_label_value
              this.graph_metric = graph_metric
              this.graph_detail_filter = filter
              this.graph_rollup_metric = graph_rollup_metric
              this.truncate_by = truncate_by
            }
          }
        )

      this.$emitter.on(ALL_EMITTERS.NESTED_METRIC_DETAIL_MODAL, ({ record, metric }) => {
        this.nested_metric_modal_open = true
        this.record = record
        this.metric = metric
      })

      this.$emitter.on(ALL_EMITTERS.SEND_EMAIL, (data) => {
        this.is_all_records = data?.is_all_records ? true : false
        this.excluded_records = data?.excluded_records?.length ? data?.excluded_records : []
        this.current_object_type = data?.object_type ? data.object_type : null
        this.total_records_count = data?.count ? data.count : null
        this.records = data?.records ? data.records : []
        this.cc_records = data?.cc_records ? data.cc_records : []
        this.record = data?.record
        this.reply_id = data?.reply_id
        this.thread_id = data?.thread_id
        this.current_object_type = data?.current_object_type
        this.content = data?.content
        this.ref_record = data?.ref_record
        this.send_email_open = true
      })

      this.$emitter.on(ALL_EMITTERS.SHOW_COMPETITOR_INSIGHTS, ({ competitor }) => {
        this.competitor = competitor
        this.insights_modal_open = true
      })

      this.$emitter.on(ALL_EMITTERS.EXPAND_PLAN, () => {
        this.plan_modal_width = "max-w-[95vw] mx-5"
      })

      this.$emitter.on(ALL_EMITTERS.COLLAPSE_PLAN, () => {
        this.plan_modal_width = "max-w-[900px] mx-5"
      })

      this.$emitter.on(ALL_EMITTERS.CREATE_NEW_PHASE, ({ journeys, selected_journey }) => {
        this.is_phase_modal_open = true
        this.journeys = journeys
        this.selected_journey = selected_journey
      })

      this.$emitter.on(ALL_EMITTERS.EDIT_PHASE, ({ phase, journeys, selected_journey }) => {
        this.is_phase_edit_modal_open = true
        this.phase = phase
        this.journeys = journeys
        this.selected_journey = selected_journey
      })

      this.$emitter.on(ALL_EMITTERS.SHOW_SUCCESS_MODAL, (message) => {
        this.success_modal_open = true
        this.success_modal_message = message
      })
      this.$emitter.on(ALL_EMITTERS.MOVE_EMAIL_TO_TRASH, ({ email, objectDeleteCallBack }) => {
        this.is_move_email_to_trash_modal_open = true
        this.to_be_deleted_email = email
        this.objectDeleteAction = (showIsDone, closeModal) => {
          dataService
            .updateInteraction(email.id, {
              meta_data: { ...email.meta_data, is_deleted: true }
            })
            .then(() => {
              closeModal()

              showIsDone()
              if (objectDeleteCallBack) {
                objectDeleteCallBack()
              }
            })
        }
      })

      this.$emitter.on(ALL_EMITTERS.RESTORE_EMAIL_FROM_TRASH, ({ email, objectDeleteCallBack }) => {
        this.is_restore_email_from_trash_modal_open = true
        this.to_be_restored_email = email
        this.objectDeleteAction = (showIsDone, closeModal) => {
          dataService
            .updateInteraction(email.id, {
              meta_data: { ...email.meta_data, is_deleted: false }
            })
            .then(() => {
              closeModal()

              showIsDone()
              if (objectDeleteCallBack) {
                objectDeleteCallBack()
              }
            })
        }
      })

      this.$emitter.on(ALL_EMITTERS.DELETE_OBJECT, ({ object: object, objectDeleteCallBack, show_confirmation = true }) => {
        this.is_delete_object_modal_open = true
        this.to_be_deleted_object = object
        if (object.type) {
          let object_data = {}
          object_data[object.type.toLowerCase()] = object
          this.objectDeleteAction = (showIsDone, closeModal) => {
            this.$store.dispatch("data/remove" + utils.capitalize(object.type), object_data).then(() => {
              if (this.confirm_delete_nested_objects) {
                this.to_be_deleted_object?.tasks?.results?.map((task) => {
                  this.$store.dispatch("data/removeTask", { task: task })
                })
                this.to_be_deleted_object?.goals?.results?.map((goal) => {
                  this.$store.dispatch("data/removeGoal", { goal: goal })
                })
                this.confirm_delete_nested_objects = false
              }
              closeModal()
              this.$emitter.emit(
                ALL_EMITTERS.ACTION_DELETED,
                object_data[object.type.toLowerCase()].due_date
                  ? object_data[object.type.toLowerCase()].due_date
                  : object_data[object.type.toLowerCase()].interaction_date
              )
              if (show_confirmation) {
                showIsDone()
              }
              if (objectDeleteCallBack) {
                objectDeleteCallBack()
              }
              this.$emitter.emit(ALL_EMITTERS[`${object.type.toUpperCase()}_REMOVED`], object)
            })
          }
        }
      })

      this.$emitter.on(ALL_EMITTERS.INTEGRATION_MISSING_MODAL, ({ message }) => {
        this.integration_missing_modal = true
        this.integration_modal_message = message
      })

      this.$emitter.on(ALL_EMITTERS.SHARE_OBJECT, ({ object: object }) => {
        this.is_share_object_open = true
        this.to_be_shared_object = object
        this.workspaces_selected = object.workspaces_shared_in
        this.to_be_shared_with_end_users = []
      })
      this.$emitter.on(ALL_EMITTERS.SHARE_PAGE, (target_page) => {
        this.share_page_is_open = true
        this.shared_page = target_page
      })
      this.$emitter.on(ALL_EMITTERS.CLONE_PAGE, (target_page) => {
        this.clone_page_is_open = true
        this.cloned_page = target_page
      })
    },
    bulkRecordsMetricUpdate(showIsDone, closeModal) {
      this.$store
        .dispatch("data/bulkRecordsMetricUpdate", {
          records: this.records,
          new_value: this.target_bulk_update_metric_value,
          metric: this.target_bulk_update_metric,
          excluded_records: this.excluded_records,
          is_all_records: this.is_all_records,
          object_type: this.current_object_type
        })
        .then(() => {
          closeModal()
          this.$toast.open({
            message:
              this.$t("Bulk update of the metric") +
              ` <strong>${this.$utils.getMetricById(this.target_bulk_update_metric).name}</strong> ` +
              this.$t("for the selected records has started"),
            position: "top",
            type: "success",
            duration: 5000
          })
          this.target_bulk_update_metric = null
          this.target_bulk_update_metric_value = null
        })
        .catch(() => {
          closeModal()
          this.$toast.open({
            message:
              this.$t("Unable to update the metric") +
              ` <strong>${this.$utils.getMetricById(this.target_bulk_update_metric).name}</strong> ` +
              this.$t("for the selected records"),
            position: "top",
            type: "error",
            duration: 5000
          })
          this.target_bulk_update_metric = null
          this.target_bulk_update_metric_value = null
        })
    },
    mergeMetricsValues(showIsDone, closeModal) {
      this.$store
        .dispatch("data/mergeMetricsValues", {
          records: this.records,
          metric_a: this.merge_metric_a,
          metric_b: this.merge_metric_b,
          merge_m: this.merge_metric_b,
          excluded_records: this.excluded_records,
          is_all_records: this.is_all_records,
          object_type: this.current_object_type
        })
        .then(() => {
          closeModal()
          this.$toast.open({
            message:
              this.$t("Merge of the metrics") +
              ` <strong>${this.$utils.getMetricById(this.merge_metric_a).name}</strong> ` +
              this.$t("and") +
              ` <strong>${this.$utils.getMetricById(this.merge_metric_b).name}</strong> ` +
              this.$t("for the selected records has started"),
            position: "top",
            type: "success",
            duration: 5000
          })
          this.merge_metric_a = null
          this.merge_metric_b = null
          this.merge_metrics_priority = null
        })
        .catch(() => {
          closeModal()
          this.$toast.open({
            message:
              this.$t("Unable to merge the metrics") +
              ` <strong>${this.$utils.getMetricById(this.merge_metric_a).name}</strong> ` +
              this.$t("and") +
              ` <strong>${this.$utils.getMetricById(this.merge_metric_b).name}</strong> ` +
              this.$t("for the selected records"),
            position: "top",
            type: "error",
            duration: 5000
          })
          this.merge_metric_a = null
          this.merge_metric_b = null
          this.merge_metrics_priority = null
        })
    },

    deleteSelectedRecords(showIsDone) {
      this.$store
        .dispatch("data/bulkRecordsDelete", {
          records: this.records,
          filter: this.current_filter,
          excluded_records: this.excluded_records,
          is_all_records: this.is_all_records,
          object_type: this.current_object_type
        })
        .then(() => {
          this.$emitter.emit(ALL_EMITTERS.BULK_DELETED, this.records)
          this.$emitter.emit(ALL_EMITTERS.REFRESH_TABLE)
          showIsDone()
        })
    },
    confirmDeleteGraph(closeModal, showIsDone) {
      this.$store
        .dispatch("data/removeGraph", this.current_graph.id)
        .then(() => {
          this.$emitter.emit(ALL_EMITTERS.GRAPH_DELETED_IN_ADD_NEW_COMPONENTS_MODAL, this.current_graph.id)
          this.$emitter.emit(ALL_EMITTERS.GRAPH_DELETED_IN_LAYOUT, this.current_graph.id)
          this.$emitter.emit(ALL_EMITTERS.GRAPH_DELETED_IN_GRAPHS_GRID, this.current_graph.id)

          closeModal()
          showIsDone()
        })
        .catch(() => {
          this.$toast.open({
            message: this.$t("You don't have permission to delete the graph") + ` <strong>${this.current_graph.name}</strong>`,
            position: "top",
            type: "error",
            duration: 3000
          })
        })
    },

    confirmDeletePage(closeModal, showIsDone) {
      this.$emitter.emit(ALL_EMITTERS.CONFIRM_DELETE_PAGE, this.page_to_be_deleted)
      this.$emitter.emit(ALL_EMITTERS.CONFIRM_DELETE_PAGE_IN_TABS, this.page_to_be_deleted)
      closeModal()
      showIsDone()
    },
    confirmDeleteExpnasionList(showIsDone) {
      this.$emitter.emit(ALL_EMITTERS.DELETE_EXPANSION_LIST_IN_EXPANSION_LIST_COMPONENT, this.expansion_list)
      this.$emitter.emit(ALL_EMITTERS.DELETE_EXPANSION_LIST_IN_STATIC_EXPANSION_LIST, this.expansion_list)
      showIsDone()
    },

    confirmRemoveFromExpnasionList(showIsDone) {
      let new_records = this.expansion_list.records.filter((r) => !this.records.includes(r.id))
      if (new_records.length == 0) {
        this.$emitter.emit(ALL_EMITTERS.DELETE_EXPANSION_LIST_IN_EXPANSION_LIST_COMPONENT, this.expansion_list)
        this.$emitter.emit(ALL_EMITTERS.DELETE_EXPANSION_LIST_IN_STATIC_EXPANSION_LIST, this.expansion_list)
      } else {
        dataService
          .updateExpansionList(this.expansion_list.id, {
            records: new_records.map((r) => r.id)
          })
          .then((res) => {
            this.$emitter.emit(ALL_EMITTERS.GET_LIST_IN_EXPANSION_LIST_COMPONENT, res.data)
            this.$emitter.emit(ALL_EMITTERS.GET_LIST_IN_STATIC_EXPANSION_LIST, res.data)
          })
      }
      showIsDone()
    },

    closeOnBoarding() {
      this.is_onboarding_modal_open = false
      this.$store.dispatch("ui/setUIValue", {
        hide_onboarding_popup: this.hide_onboarding_modal
      })
    },

    updateForecastTableMetrics(showIsDone, closeModal) {
      if (this.updated_forecast_table_metrics) {
        if (
          !this.$utils.isEqual(
            this.getForecastTableMetrics().map((x) => x.id),
            this.updated_forecast_table_metrics.map((x) => x.id)
          )
        ) {
          this.$store.dispatch("ui/setUIValue", {
            forecast_table_headers: {
              ...this.$store.state.ui_options.forecast_table_headers,
              [this.component_reference]: this.updated_forecast_table_metrics.map((x) => x.id)
            }
          })
          showIsDone()
          closeModal()
        } else {
          //close modal if no selection is made by the user
          closeModal()
        }
      } else {
        //close modal if selection user makes is same as current selection
        closeModal()
      }
    },

    updateLeadsTableMetrics(showIsDone, closeModal) {
      if (this.updated_leads_table_metrics.length != 0) {
        if (
          !this.$utils.isEqual(
            this.getCurrentLeadsTableMetrics().map((x) => x.id),
            this.updated_leads_table_metrics.map((x) => x.id)
          )
        ) {
          this.$store.dispatch("ui/setUIValue", {
            leads_table_headers: {
              ...this.$store.state.ui_options.leads_table_headers,
              [this.component_reference]: this.updated_leads_table_metrics.map((x) => x.id)
            }
          })
          showIsDone()
          closeModal()
        } else {
          //close modal if no selection is made by the user
          closeModal()
        }
      } else {
        //close modal if selection user makes is same as current selection
        closeModal()
      }
    },
    updateActivitiesTableHeaders(showIsDone, closeModal) {
      if (this.updated_activities_table_headers.length != 0) {
        if (
          !this.$utils.isEqual(
            this.getCurrentActivitiesTableHeaders().map((x) => x.id),
            this.updated_activities_table_headers.map((x) => x.id)
          )
        ) {
          if (this.type == "Plans") {
            this.$store.dispatch("ui/setUIValue", {
              plans_activities_table_headers: {
                ...this.$store.state.ui_options.plans_activities_table_headers,
                [this.component_reference]: this.updated_activities_table_headers.map((x) => x.id)
              }
            })
          } else if (this.type == "Goals") {
            this.$store.dispatch("ui/setUIValue", {
              goals_activities_table_headers: {
                ...this.$store.state.ui_options.goals_activities_table_headers,
                [this.component_reference]: this.updated_activities_table_headers.map((x) => x.id)
              }
            })
          } else if (this.type == "Tasks") {
            this.$store.dispatch("ui/setUIValue", {
              tasks_activities_table_headers: {
                ...this.$store.state.ui_options.tasks_activities_table_headers,
                [this.component_reference]: this.updated_activities_table_headers.map((x) => x.id)
              }
            })
          } else if (this.type == "Interactions") {
            this.$store.dispatch("ui/setUIValue", {
              interactions_activities_table_headers: {
                ...this.$store.state.ui_options.interactions_activities_table_headers,
                [this.component_reference]: this.updated_activities_table_headers.map((x) => x.id)
              }
            })
          }
          showIsDone()
          closeModal()
        } else {
          //close modal if no selection is made by the user
          closeModal()
        }
      } else {
        //close modal if selection user makes is same as current selection
        closeModal()
      }
    },

    updateTabs(showIsDone, closeModal) {
      if (this.updated_tabs) {
        if (
          !this.$utils.isEqual(
            this.current_tabs.map((x) => x.type),
            this.updated_tabs.map((x) => x.type)
          )
        ) {
          this.setPageTabs({
            page_reference: this.current_page_reference,
            new_tabs_list: this.updated_tabs.map((x) => x.type)
          })

          // if (this.current_page_reference == "portfolio") {
          //   this.$router.push(`?object=${this.first_portfolio_tab.type}`)
          // }

          showIsDone()
          closeModal()
        } else {
          //close modal if no selection is made by the user
          closeModal()
        }
      } else {
        //close modal if selection user makes is same as current selection
        closeModal()
      }
    },

    updatePageGraphs(showIsDone, closeModal) {
      if (this.updated_graphs) {
        if (
          !this.$utils.isEqual(
            this.current_graphs.map((x) => x.order),
            this.updated_graphs.map((x) => x.order)
          )
        ) {
          this.setPageGraphs({
            page_reference: this.current_page_reference,
            object_type: this.current_object_type,
            new_graphs_list: this.updated_graphs.map((x) => x.order)
          })
          this.$emitter.emit(ALL_EMITTERS.GRAPHS_SHOW_UPDATED)
          showIsDone()
          closeModal()
        }
      } else {
        //close modal if selection user makes is same as current selection
        closeModal()
        closeModal()
      }
    },

    updateTableMetrics(showIsDone, closeModal) {
      if (
        !this.$utils.isEqual(
          this.current_table_metrics.filter((x) => x).map((x) => x.reference_name),
          this.updated_table_metrics.filter((x) => x).map((x) => x.reference_name)
        )
      ) {
        this.setViewMetrics({
          page_reference: this.current_page_reference,
          component_reference: this.component_reference,
          layout_page_reference: this.layout_page_reference,
          object_type: this.current_object_type,
          view_type: this.current_view_type,
          new_metrics_list: this.updated_table_metrics.map((x) => x.reference_name)
        })
        showIsDone()
        closeModal()
      } else {
        //close modal if no selection is made by the user
        closeModal()
      }
    },
    updateRollupMetrics(showIsDone, closeModal) {
      setTimeout(() => {
        this.setViewMetrics({
          page_reference: this.current_page_reference,
          component_reference: this.component_reference,
          layout_page_reference: this.layout_page_reference,
          object_type: this.current_object_type,
          view_type: this.current_view_type,
          current_forecast_name: this.current_forecast_name,
          new_metrics_list: this.updated_rollup_metrics.map((x) => x.reference_name)
        })
      }, 300)
      closeModal()
      showIsDone()
    },

    handleAddIntegrations(name = "google-calendar") {
      let redirect_uri_name = name == "google-calendar" ? "gmail" : null
      IntegrationService.linkService(name, {
        integration_provider: "revsetter"
      }).then((res) => {
        let integration_id = res.id
        getAuthUrl(name, {
          state: btoa(
            JSON.stringify({
              next: window.location.path,
              integration_id: integration_id
            })
          ),
          redirect_uri_name: redirect_uri_name
        }).then((auth_url) => {
          window.open(auth_url, "_blank")
        })
      })
    }
  }
}
</script>
